import { useEffect, useMemo, useRef, useState } from "react";
import { useNotification } from "./NotifPopup";
import { useConfirm } from "./ModalConfirm";
import api from "../services/axiosInstance";
import { backendPort } from "../components/Fungsiku";
import { FormasiBan } from "./FormasiBan";
import { ModalCariMT } from "./Page27_Cari";
import { PageKonten9_Pict as AttachmentPicker } from "./Page9_Pict";
import { ShowPic } from "../components/ShowPic";
import { formBulkOdo } from "../components/ExcelFormBulkOdo";
import { uploadBulkOdo } from "../components/ExcelUploadBulkOdo";

const ODO_TD_ACTIONS = [
  {
    value: "odo",
    title: "Update ODO",
    description: "Catat angka ODO terbaru unit MT.",
    submitLabel: "Simpan Update ODO",
  },
  {
    value: "td",
    title: "Update TD",
    description: "Perbarui data Tire Depth ban terpasang.",
    submitLabel: "Simpan Update TD",
  },
  {
    value: "bulk",
    title: "Update dengan Excel (Bulk)",
    description: "Upload file excel untuk update data massal.",
    submitLabel: "Upload Bulk",
  }
];

const panelAccentClassByUpdateAction = (actionValue) => {
  const value = String(actionValue || "").trim();
  if (value === "odo") return "panel-accent-lav";
  if (value === "td") return "panel-accent-sky";
  if (value === "bulk") return "panel-accent-mint";
  return "panel-accent-default";
};

const createEmptyTdRow = (posisi = "") => ({
  posisi,
  td1: "",
  td2: "",
  td3: "",
  td4: "",
  jumlahBaut: "",
  tekananSebelum: "",
  tekananSesudah: "",
  temuan: "",
  temuanAttachments: [],
  isEdited: false,
});

const REQ_GROUP_ODO_TD_LAST_ODO = "odo-td-last-odo";
const REQ_GROUP_ODO_TD_LAST_TD = "odo-td-last-td";
const REQ_GROUP_ODO_TD_BULK_TEMPLATE = "odo-td-bulk-template";
const REQ_GROUP_ODO_TD_SUBMIT_ODO = "odo-td-submit-odo";
const REQ_GROUP_ODO_TD_SUBMIT_TD = "odo-td-submit-td";
const REQ_GROUP_ODO_TD_SUBMIT_BULK = "odo-td-submit-bulk";
const MAX_ODO_DELTA_PER_DAY = 2000;
const MAX_TD_CORRECTION_MEMO_SIZE = 5 * 1024 * 1024;
const TD_CORRECTION_MEMO_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
]);
const ODO_FLAG_TRANSACTIONS = new Set(["odo_rusak", "mt_off"]);
const buildKoreksiOdoHariIniBulkNotice = (nopol) => (
  `ODO yang telah dikoreksi hari ini tidak dapat diupdate melalui menu bulk, kosongkan/skip input odo untuk MT ${String(nopol || "-").trim() || "-"}`
);

const isOdoFlagTransaction = (value) => ODO_FLAG_TRANSACTIONS.has(String(value || "").trim());
const isOdoCatatanTransaction = (value) => String(value || "normal").trim() !== "koreksi_odo";
const getOdoFlagFromReff = (reffRaw) => {
  const reffObj = (() => {
    if (!reffRaw) return {};
    if (typeof reffRaw === "string") {
      try {
        const parsed = JSON.parse(reffRaw);
        return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
      } catch {
        return {};
      }
    }
    return typeof reffRaw === "object" && !Array.isArray(reffRaw) ? reffRaw : {};
  })();
  const kondisi = String(reffObj?.flag?.kondisi || "").trim();
  return kondisi === "normal" || isOdoFlagTransaction(kondisi)
    ? { kondisi, catatan: String(reffObj?.flag?.catatan || "") }
    : { kondisi: "normal", catatan: "" };
};
const getOdoDriverFromReff = (reffRaw) => {
  if (!reffRaw) return "";
  if (typeof reffRaw === "string") {
    try {
      const parsed = JSON.parse(reffRaw);
      return String(parsed?.sopir || "").trim();
    } catch {
      return "";
    }
  }
  return typeof reffRaw === "object" && !Array.isArray(reffRaw)
    ? String(reffRaw?.sopir || "").trim()
    : "";
};

export function PageKonten15OdoTd({
  authIdentity = {},
  globalOpsi,
  resendToken = 0,
  mode = "page",
  forcedAction = "",
  presetMtId = "",
  presetKodeCabang = "",
  presetTanggal = "",
  onClose = null,
  onAfterSave = null,
}) {
  const { showNotif } = useNotification();
  const { showConfirm } = useConfirm();
  const actorKodeCabang = String(authIdentity?.kode_cabang || "").trim();
  const actorNama = String(authIdentity?.nama || "").trim();
  const initialUserCabang = actorKodeCabang;
  const initialIsPusat = initialUserCabang === "C-1";
  const defaultKodeCabang = initialIsPusat ? "" : initialUserCabang;
  const [activeAction, setActiveAction] = useState(forcedAction || "");
  const defaultTanggal = useMemo(() => {
    const now = new Date();
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, "0");
    const d = String(now.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, []);
  const [panelForm, setPanelForm] = useState({
    odo: {
      kodeCabang: defaultKodeCabang,
      tanggal: defaultTanggal,
      mt: "",
      odoInput: "",
      namaDriver: "",
      jenisTransaksi: "normal",
      alasanKoreksi: "",
      catatanFlag: "",
    },
    td: {
      kodeCabang: defaultKodeCabang,
      tanggal: defaultTanggal,
      mt: "",
      activePosisi: "",
      activeRowIndex: 0,
      tdNoSignificantChange: false,
      tdRows: [createEmptyTdRow()],
    },
    bulk: {
      kodeCabang: defaultKodeCabang,
      tanggal: defaultTanggal,
      file: null,
    },
  });
  const [bulkTemplateState, setBulkTemplateState] = useState({
    loading: false,
    rows: [],
    count: 0,
    namaCabang: "",
  });
  const [bulkUploadState, setBulkUploadState] = useState({
    parsing: false,
    valid: [],
    invalid: [],
    totalReadRows: 0,
    kmHeader: "",
    headerDate: "",
    headerError: "",
    validated: false,
  });
  const [lastUpdate, setLastUpdate] = useState({
    odo: { loading: false, data: null },
  });
  const [tdLastUpdate, setTdLastUpdate] = useState({
    loading: false,
    rows: [],
    banPositionMap: {},
    kapasitas: "-",
    kategori: "-",
    roda: 16,
    cadangan: 3,
  });
  const [showCariMtModal, setShowCariMtModal] = useState(false);
  const [mtPickerTarget, setMtPickerTarget] = useState("");
  const [submitLocked, setSubmitLocked] = useState({ odo: false, td: false, bulk: false });
  const [attachmentsOdo, setAttachmentsOdo] = useState([]);
  const [invalidCaptionIndexesOdo, setInvalidCaptionIndexesOdo] = useState([]);
  const [tdCorrectionModal, setTdCorrectionModal] = useState({
    open: false,
    posisi: "",
    alasan: "",
    td1: "",
    td2: "",
    td3: "",
    td4: "",
    memo: null,
  });
  const [tdTemuanModal, setTdTemuanModal] = useState({
    open: false,
    posisi: "",
    temuan: "",
    attachments: [],
  });
  const [tdLastTemuanModal, setTdLastTemuanModal] = useState({
    open: false,
    posisi: "",
    temuan: "",
    files: [],
  });
  const [tdLastTemuanPreviewOpen, setTdLastTemuanPreviewOpen] = useState(false);
  const [tdBanTooltip, setTdBanTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    posisi: "",
    id_ban: 0,
  });
  const [tdFormasiPopup, setTdFormasiPopup] = useState({
    open: false,
    x: 0,
    y: 96,
    dragging: false,
    dragOffsetX: 0,
    dragOffsetY: 0,
  });
  const [bulkRefreshTick, setBulkRefreshTick] = useState(0);
  const bulkFileInputRef = useRef(null);

  const todayText = useMemo(
    () =>
      new Date().toLocaleDateString("id-ID", {
        weekday: "long",
        day: "2-digit",
        month: "long",
        year: "numeric",
      }),
    []
  );

  const opsiCabang = globalOpsi?.opsiCabang || [];
  const opsiMtSemua = globalOpsi?.opsiMT || [];
  const isPusat = actorKodeCabang === "C-1";
  const namaCabangUser = useMemo(() => {
    if (isPusat) return "Pusat";
    return (
      (Array.isArray(opsiCabang) ? opsiCabang : []).find(
        (x) => String(x?.kode_cabang || "").trim() === actorKodeCabang
      )?.nama_cabang || actorKodeCabang || "-"
    );
  }, [actorKodeCabang, isPusat, opsiCabang]);

  const currentAction = forcedAction || activeAction;
  const selectedAction = ODO_TD_ACTIONS.find((x) => x.value === currentAction);
  const isModalMode = mode === "modal";
  const isPageModeTdAction = !forcedAction && !isModalMode && currentAction === "td";
  const isPageModeTdExpanded = isPageModeTdAction
    && Boolean(String(panelForm?.td?.kodeCabang || "").trim())
    && Boolean(String(panelForm?.td?.mt || "").trim());

  useEffect(() => {
    const presetIsPusat = String(presetKodeCabang || "").trim() === "C-1";
    const normalizedPresetCabang = presetIsPusat ? "" : String(presetKodeCabang || "").trim();
    const fallbackCabang = actorKodeCabang === "C-1" ? "" : actorKodeCabang;
    setPanelForm((prev) => ({
      ...prev,
      odo: {
        ...prev.odo,
        kodeCabang: normalizedPresetCabang || fallbackCabang,
        tanggal: presetTanggal || prev.odo.tanggal || defaultTanggal,
        mt: presetMtId || prev.odo.mt || "",
      },
      td: {
        ...prev.td,
        kodeCabang: normalizedPresetCabang || fallbackCabang,
        tanggal: presetTanggal || prev.td.tanggal || defaultTanggal,
        mt: presetMtId || prev.td.mt || "",
      },
      bulk: {
        ...prev.bulk,
        kodeCabang: normalizedPresetCabang || fallbackCabang,
        tanggal: presetTanggal || prev.bulk.tanggal || defaultTanggal,
      },
    }));
  }, [actorKodeCabang, defaultTanggal, presetKodeCabang, presetTanggal, presetMtId]);

  useEffect(() => {
    if (!forcedAction) return;
    setActiveAction(forcedAction);
  }, [forcedAction]);

  useEffect(() => {
    setTdBanTooltip((prev) => ({ ...prev, visible: false }));
  }, [currentAction, panelForm.td.mt]);

  const selectedOdoMt = opsiMtSemua.find((x) => x.id_mt === panelForm.odo.mt) || null;
  const selectedTdMt = opsiMtSemua.find((x) => x.id_mt === panelForm.td.mt) || null;
  const tdInputRowByPosisi = useMemo(() => {
    const map = new Map();
    (Array.isArray(panelForm?.td?.tdRows) ? panelForm.td.tdRows : []).forEach((row) => {
      const posisi = String(row?.posisi || "").trim();
      if (posisi) map.set(posisi, row);
    });
    return map;
  }, [panelForm?.td?.tdRows]);
  const tdLastRowByPosisi = useMemo(() => {
    const map = new Map();
    (Array.isArray(tdLastUpdate?.rows) ? tdLastUpdate.rows : []).forEach((row) => {
      const posisi = String(row?.posisi || "").trim();
      if (posisi) map.set(posisi, row);
    });
    return map;
  }, [tdLastUpdate?.rows]);
  const selectedCurrentMt = useMemo(() => {
    if (currentAction !== "odo" && currentAction !== "td") return null;
    const mtId = String(panelForm[currentAction]?.mt || "").trim();
    if (!mtId) return null;
    return opsiMtSemua.find((x) => String(x?.id_mt || "").trim() === mtId) || null;
  }, [currentAction, panelForm, opsiMtSemua]);
  const isMtLockedFromParent = useMemo(
    () => Boolean(String(presetMtId || "").trim()),
    [presetMtId]
  );

  const formatRibuan = (value) => Number(value || 0).toLocaleString("id-ID");
  const formatKmTooltip = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "-";
    return num.toLocaleString("id-ID");
  };
  const resolveSeriBan = (...sources) => {
    for (const source of sources) {
      if (!source || typeof source !== "object") continue;
      const seri = String(
        source?.seri_ban
        || source?.seri
        || source?.no_seri
        || source?.nomor_seri
        || source?.kode_ban
        || ""
      ).trim();
      if (seri) return seri;
    }
    return "";
  };
  const getTooltipPositionStyle = (x, y) => {
    const tooltipWidth = 280;
    const tooltipHeight = 132;
    const offset = 14;
    const margin = 8;
    const viewportWidth = typeof window !== "undefined" ? Number(window.innerWidth || 0) : 0;
    const viewportHeight = typeof window !== "undefined" ? Number(window.innerHeight || 0) : 0;
    const rawX = Number(x || 0);
    const rawY = Number(y || 0);
    const preferRight = rawX + offset + tooltipWidth <= viewportWidth - margin;
    const preferBottom = rawY + offset + tooltipHeight <= viewportHeight - margin;
    const left = preferRight
      ? rawX + offset
      : Math.max(margin, rawX - tooltipWidth - offset);
    const top = preferBottom
      ? rawY + offset
      : Math.max(margin, rawY - tooltipHeight - offset);
    return { left: `${left}px`, top: `${top}px` };
  };
  const formatYYYYMMDD = (value) => {
    if (!value) return null;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };
  const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("id-ID");
  };
  const isTodayDate = (value) => {
    const todayKey = formatYYYYMMDD(new Date());
    return Boolean(todayKey) && formatYYYYMMDD(value) === todayKey;
  };
  const getDaySpanAtLeastOne = (fromDate, toDate) => {
    const fromKey = formatYYYYMMDD(fromDate);
    const toKey = formatYYYYMMDD(toDate);
    if (!fromKey || !toKey) return 1;
    const fromObj = new Date(`${fromKey}T00:00:00`);
    const toObj = new Date(`${toKey}T00:00:00`);
    const diffMs = toObj.getTime() - fromObj.getTime();
    if (!Number.isFinite(diffMs)) return 1;
    const diffDays = Math.floor(diffMs / 86400000);
    return Math.max(1, diffDays);
  };
  const parseDaftarBan = (raw) => {
    if (!raw) return {};
    if (typeof raw === "string") {
      try {
        const parsed = JSON.parse(raw);
        return typeof parsed === "object" && parsed ? parsed : {};
      } catch {
        return {};
      }
    }
    return typeof raw === "object" && raw ? raw : {};
  };
  const parseJsonValue = (raw, fallback = null) => {
    if (raw == null || raw === "") return fallback;
    if (typeof raw === "string") {
      try {
        return JSON.parse(raw);
      } catch {
        return fallback;
      }
    }
    return raw;
  };
  const firstNonEmptyText = (...values) => {
    for (const value of values) {
      const text = String(value ?? "").trim();
      if (text) return text;
    }
    return "";
  };
  const normalizeTdLastTemuanFiles = (...sources) => {
    const rawItems = sources.flatMap((source) => {
      const parsed = parseJsonValue(source, source);
      if (!parsed) return [];
      if (Array.isArray(parsed)) return parsed;
      if (typeof parsed === "object") {
        if (Array.isArray(parsed?.lampiran)) return parsed.lampiran;
        if (Array.isArray(parsed?.dokumen)) return parsed.dokumen;
        if (Array.isArray(parsed?.attachments)) return parsed.attachments;
        return [parsed];
      }
      const text = String(parsed || "").trim();
      return /^(https?:\/\/|\/uploads\/)/i.test(text) || /\.(png|jpe?g|webp|gif|bmp|svg|pdf)($|\?)/i.test(text)
        ? [{ path: text }]
        : [];
    });

    return rawItems
      .map((item, index) => {
        const rawPath = String(item?.path || item?.url || item?.file_path || item?.file || "").trim();
        if (!rawPath) return null;
        const url = rawPath.startsWith("http")
          ? rawPath
          : rawPath.startsWith("/")
            ? `${backendPort}${rawPath}`
            : `${backendPort}/uploads/dokumen/${rawPath}`;
        const caption = String(item?.caption || item?.keterangan || item?.name || `Dokumen ${index + 1}`).trim();
        return {
          url,
          caption,
          name: String(item?.name || caption || `Dokumen ${index + 1}`).trim(),
          note: caption,
        };
      })
      .filter(Boolean);
  };
  const resolveTdLastTemuan = (kmtd = {}, banRow = {}) => {
    const reffObj = parseDaftarBan(kmtd?.reff || banRow?.reff || {});
    const inspeksiObj = parseDaftarBan(reffObj?.inspeksi || {});
    const temuan = firstNonEmptyText(
      kmtd?.temuan,
      kmtd?.catatan_temuan,
      kmtd?.catatan,
      kmtd?.keterangan,
      inspeksiObj?.catatan,
      reffObj?.temuan,
      reffObj?.catatan_temuan,
      reffObj?.catatan,
      reffObj?.keterangan,
      banRow?.temuan,
      banRow?.catatan_temuan,
      banRow?.catatan
    );
    const files = normalizeTdLastTemuanFiles(
      kmtd?.lampiran,
      kmtd?.dokumen,
      kmtd?.attachments,
      inspeksiObj?.lampiran,
      reffObj?.lampiran,
      reffObj?.dokumen,
      reffObj?.attachments,
      banRow?.lampiran,
      banRow?.dokumen,
      banRow?.attachments
    );
    return { temuan, files };
  };
  const hasReffHistory = (reffRaw, key) => {
    const reffObj = parseDaftarBan(reffRaw);
    const value = reffObj?.[key];
    return Array.isArray(value) ? value.length > 0 : Boolean(value);
  };
  const parseTdParts = (tdValue) => {
    const rawText = String(tdValue || "").trim();
    if (!rawText) return null;
    const matches = rawText.match(/\d+(?:[.,]\d+)?/g);
    if (!matches || matches.length !== 4) return null;
    const parsed = matches.map((x) => Number(String(x).replace(",", ".")));
    if (parsed.some((x) => !Number.isFinite(x))) return null;
    return parsed;
  };

  const getTdCadanganDefaultValue = (posisi, field) => {
    const posisiText = String(posisi || "").trim().toUpperCase();
    if (!posisiText.startsWith("S-")) return "";
    const prevRow = tdLastRowByPosisi.get(String(posisi || "").trim());
    const prevParts = parseTdParts(prevRow?.td);
    if (!prevParts) return "";
    const fieldIndexMap = { td1: 0, td2: 1, td3: 2, td4: 3 };
    const idx = fieldIndexMap[field];
    if (idx == null) return "";
    const value = prevParts[idx];
    return Number.isFinite(value) ? String(value) : "";
  };

  const getTdEffectiveFieldValue = (row, field) => {
    if (!row?.tdCorrection?.valid && isTdNormalLockedByCorrection(row?.posisi)) {
      const prevParts = getLastTdPartsByPosisi(row?.posisi);
      const fieldIndexMap = { td1: 0, td2: 1, td3: 2, td4: 3 };
      const idx = fieldIndexMap[field];
      return idx == null || !prevParts ? "" : String(prevParts[idx] ?? "");
    }
    const directValue = String(row?.[field] ?? "").trim();
    if (row?.touchedFields?.[field]) return directValue;
    if (directValue !== "") return directValue;
    return getTdCadanganDefaultValue(row?.posisi, field);
  };

  const buildTdRowsForSubmit = (rows) => {
    const inputRows = Array.isArray(rows) ? rows : [];
    const rowMap = new Map();
    inputRows.forEach((row) => {
      const posisi = String(row?.posisi || "").trim();
      if (!posisi) return;
      rowMap.set(posisi, row);
    });

    // Pastikan ban cadangan (S-*) yang tampil default ikut diproses saat submit.
    (Array.isArray(tdLastUpdate.rows) ? tdLastUpdate.rows : []).forEach((row) => {
      const posisi = String(row?.posisi || "").trim();
      if (!posisi.toUpperCase().startsWith("S-") && !isTdNormalLockedByCorrection(posisi)) return;
      if (!rowMap.has(posisi)) {
        rowMap.set(posisi, createEmptyTdRow(posisi));
      }
    });

    const normalizedRows = Array.from(rowMap.values()).map((row) => ({
      posisi: String(row?.posisi || "").trim(),
      td1: getTdEffectiveFieldValue(row, "td1"),
      td2: getTdEffectiveFieldValue(row, "td2"),
      td3: getTdEffectiveFieldValue(row, "td3"),
      td4: getTdEffectiveFieldValue(row, "td4"),
      jumlahBaut: String(row?.jumlahBaut || "").trim(),
      tekananSebelum: String(row?.tekananSebelum || "").trim(),
      tekananSesudah: String(row?.tekananSesudah || "").trim(),
      temuan: String(row?.temuan || "").trim(),
      temuanAttachments: Array.isArray(row?.temuanAttachments) ? row.temuanAttachments : [],
      tdCorrection: row?.tdCorrection || null,
    }));
    const rowsWithAnyValue = normalizedRows.filter((row) => (
      row.td1 !== "" || row.td2 !== "" || row.td3 !== "" || row.td4 !== ""
    ));
    const hasPartialRow = rowsWithAnyValue.some((row) => (
      row.td1 === "" || row.td2 === "" || row.td3 === "" || row.td4 === ""
    ));
    const fullRows = rowsWithAnyValue.filter((row) => (
      row.td1 !== "" && row.td2 !== "" && row.td3 !== "" && row.td4 !== ""
    ));
    return {
      rowsWithAnyValue,
      hasPartialRow,
      fullRows,
    };
  };

  const loadLastUpdate = async (panelKey, idMt) => {
    if (!idMt) {
      if (panelKey === "odo") {
        setLastUpdate((prev) => ({ ...prev, [panelKey]: { loading: false, data: null } }));
      }
      return;
    }

    if (panelKey !== "odo") return;
    setLastUpdate((prev) => ({ ...prev, odo: { loading: true, data: null } }));
    try {
      api.cancelPendingRequests({ group: REQ_GROUP_ODO_TD_LAST_ODO, reason: "Refresh last odo" });
      const { data } = await api.get(`${backendPort}/api/main/load_last_check/${idMt}`, {
        requestGroup: REQ_GROUP_ODO_TD_LAST_ODO,
        timeout: 120000,
      });
      const odoRow = (data && typeof data === "object" && data.mt_odo && typeof data.mt_odo === "object")
        ? data.mt_odo
        : null;
      const odoGate = (data && typeof data === "object" && data.odo_gate && typeof data.odo_gate === "object")
        ? data.odo_gate
        : null;
      const odoFlagDefault = getOdoFlagFromReff(odoRow?.reff);
      setLastUpdate((prev) => ({
        ...prev,
        odo: {
          loading: false,
          data: {
            tanggal: odoRow?.tanggal || null,
            odo: Number(odoRow?.odo || 0),
            kmt: Number(odoRow?.kmt_mt || 0),
            reff: odoRow?.reff || null,
            odoGate,
            hasKoreksiOdo: isTodayDate(odoRow?.tanggal) && hasReffHistory(odoRow?.reff, "koreksi_odo"),
          },
        },
      }));
      setPanelForm((prev) => ({
        ...prev,
        odo: {
          ...prev.odo,
          jenisTransaksi: odoFlagDefault.kondisi,
          alasanKoreksi: "",
          catatanFlag: odoFlagDefault.catatan,
          namaDriver: getOdoDriverFromReff(odoRow?.reff),
        },
      }));
    } catch (err) {
      if (api.isCanceledError(err)) {
        setLastUpdate((prev) => ({ ...prev, odo: { loading: false, data: null } }));
        return;
      }
      console.error(err);
      setLastUpdate((prev) => ({ ...prev, odo: { loading: false, data: null } }));
    }
  };

  const loadTdLastUpdate = async (idMt) => {
    if (!idMt) {
      setTdLastUpdate({ loading: false, rows: [], banPositionMap: {}, kapasitas: "-", kategori: "-", roda: 16, cadangan: 3 });
      setPanelForm((prev) => ({
        ...prev,
        td: {
          ...prev.td,
          activePosisi: "",
          activeRowIndex: 0,
          tdNoSignificantChange: false,
          tdRows: [createEmptyTdRow()],
        },
      }));
      return;
    }

    setTdLastUpdate({ loading: true, rows: [], banPositionMap: {}, kapasitas: "-", kategori: "-", roda: 16, cadangan: 3 });
    try {
      api.cancelPendingRequests({ group: REQ_GROUP_ODO_TD_LAST_TD, reason: "Refresh last td" });
      const requestConfig = {
        requestGroup: REQ_GROUP_ODO_TD_LAST_TD,
        timeout: 120000,
      };
      const { data: lastCheckRaw } = await api.get(`${backendPort}/api/main/load_last_check/${idMt}`, requestConfig);
      const lastCheck = (lastCheckRaw && typeof lastCheckRaw === "object") ? lastCheckRaw : {};
      const odoRow = (lastCheck.mt_odo && typeof lastCheck.mt_odo === "object") ? lastCheck.mt_odo : null;
      const banRows = Array.isArray(lastCheck.ban_initial) ? lastCheck.ban_initial : [];
      const kmtdRows = Array.isArray(lastCheck.ban_last_kmtd) ? lastCheck.ban_last_kmtd : [];
      let daftarBan = parseDaftarBan(odoRow?.daftar_ban || {});
      if (!Object.keys(daftarBan).length) {
        const fromInitial = {};
        banRows.forEach((row) => {
          const posisiRaw = String(row?.posisi || "").trim();
          const posisi = posisiRaw.includes("#")
            ? String(posisiRaw.split("#").slice(1).join("#") || "").trim()
            : posisiRaw;
          const idBan = Number(row?.id_ban || 0);
          if (posisi && Number.isFinite(idBan) && idBan > 0) fromInitial[posisi] = idBan;
        });
        daftarBan = fromInitial;
      }
      const positionEntries = Object.entries(daftarBan || {}).sort((a, b) => a[0].localeCompare(b[0]));
      const positionBanIds = Array.from(new Set(
        positionEntries
          .map(([, idBan]) => Number(idBan || 0))
          .filter((idBan) => Number.isFinite(idBan) && idBan > 0)
      ));

      const byId = {};
      banRows.forEach((row) => {
        const idBan = Number(row?.id_ban || 0);
        if (!idBan || byId[idBan]) return;
        byId[idBan] = row;
      });
      const missingSeriIds = positionBanIds.filter((idBan) => !resolveSeriBan(byId[idBan]));
      if (missingSeriIds.length) {
        try {
          const { data: metaRows } = await api.post(
            `${backendPort}/api/main/load_daftarBanThisMT_initial`,
            { kodeBan: missingSeriIds },
            {
              requestGroup: REQ_GROUP_ODO_TD_LAST_TD,
              timeout: 120000,
            }
          );
          (Array.isArray(metaRows) ? metaRows : []).forEach((row) => {
            const idBan = Number(row?.id_ban || row?.id || 0);
            if (!idBan) return;
            byId[idBan] = { ...(byId[idBan] || {}), ...row };
          });
        } catch (metaErr) {
          if (!api.isCanceledError(metaErr)) {
            console.warn("Load metadata ban tooltip gagal", metaErr);
          }
        }
      }
      const kmtdById = {};
      kmtdRows.forEach((row) => {
        const idBan = Number(row?.id_ban || 0);
        if (!idBan) return;
        kmtdById[idBan] = row;
      });

      const rows = positionEntries.map(([posisi, idBan]) => {
        const numericIdBan = Number(idBan);
        const row = byId[numericIdBan] || {};
        const kmtd = kmtdById[numericIdBan] || null;
        const reffObj = parseDaftarBan(kmtd?.reff || row?.reff || {});
        const inspeksiObj = parseDaftarBan(reffObj?.inspeksi || {});
        const lastTemuan = resolveTdLastTemuan(kmtd || {}, row || {});
        return {
          posisi,
          id_ban: numericIdBan,
          seri: resolveSeriBan(row, kmtd) || "-",
          merk: row?.merk || "-",
          tanggal: kmtd?.tgltd || kmtd?.tgl || row?.tanggal || null,
          td: kmtd?.td || row?.td || "-",
          bautLast: inspeksiObj?.baut ?? kmtd?.jumlah_baut ?? kmtd?.baut ?? row?.jumlah_baut ?? row?.baut ?? "-",
          tekananLast: inspeksiObj?.psi_1 ?? kmtd?.tekanan_ban ?? kmtd?.tekanan ?? kmtd?.psi ?? row?.tekanan_ban ?? row?.tekanan ?? row?.psi ?? "-",
          lastTemuan: lastTemuan.temuan,
          lastTemuanFiles: lastTemuan.files,
          kmt: kmtd?.kmt ?? row?.kmt ?? null,
          reff: kmtd?.reff || null,
        };
      });

      const banPositionMap = {};
      positionEntries.forEach(([posisi, idBan]) => {
        banPositionMap[posisi] = Number(idBan) || 0;
      });

      const firstPosisi = rows[0]?.posisi || "";
      setTdLastUpdate({
        loading: false,
        rows,
        banPositionMap,
        kapasitas: String(odoRow?.kapasitas || "-"),
        kategori: String(odoRow?.kategori || "-"),
        roda: Number(odoRow?.roda || selectedTdMt?.roda || 16),
        cadangan: Number(odoRow?.cadangan || selectedTdMt?.cadangan || 3),
      });
      setPanelForm((prev) => ({
        ...prev,
        td: {
          ...prev.td,
          activePosisi: firstPosisi,
          activeRowIndex: 0,
          tdNoSignificantChange: false,
          tdRows: [createEmptyTdRow(firstPosisi)],
        },
      }));
    } catch (err) {
      if (api.isCanceledError(err)) {
        setTdLastUpdate({ loading: false, rows: [], banPositionMap: {}, kapasitas: "-", kategori: "-", roda: 16, cadangan: 3 });
        return;
      }
      console.error(err);
      setTdLastUpdate({ loading: false, rows: [], banPositionMap: {}, kapasitas: "-", kategori: "-", roda: 16, cadangan: 3 });
    }
  };

  useEffect(() => {
    loadLastUpdate("odo", panelForm.odo.mt);
  }, [panelForm.odo.mt, resendToken]);

  useEffect(() => {
    loadTdLastUpdate(panelForm.td.mt);
  }, [panelForm.td.mt, resendToken]);

  useEffect(() => {
    const shouldLoad = currentAction === "bulk"
      && Boolean(panelForm?.bulk?.kodeCabang)
      && Boolean(panelForm?.bulk?.tanggal);

    if (!shouldLoad) {
      setBulkTemplateState({ loading: false, rows: [], count: 0, namaCabang: "" });
      return;
    }

    let isCancelled = false;
    const loadBulkTemplateData = async () => {
      setBulkTemplateState((prev) => ({ ...prev, loading: true }));
      try {
        api.cancelPendingRequests({ group: REQ_GROUP_ODO_TD_BULK_TEMPLATE, reason: "Refresh bulk template odo" });
        const payload = {
          kode_cabang: panelForm.bulk.kodeCabang,
          tanggal: panelForm.bulk.tanggal,
        };
        const { data } = await api.post(`${backendPort}/api/main/load_bulk_odo_template_data`, payload, {
          requestGroup: REQ_GROUP_ODO_TD_BULK_TEMPLATE,
          timeout: 120000,
        });
        if (isCancelled) return;
        const rows = Array.isArray(data?.rows) ? data.rows : [];
        setBulkTemplateState({
          loading: false,
          rows,
          count: Number(data?.count || rows.length || 0),
          namaCabang: String(data?.nama_cabang || "").trim(),
        });
      } catch (err) {
        if (isCancelled) return;
        if (api.isCanceledError(err)) {
          setBulkTemplateState({ loading: false, rows: [], count: 0, namaCabang: "" });
          return;
        }
        console.error(err);
        setBulkTemplateState({ loading: false, rows: [], count: 0, namaCabang: "" });
        showNotif("error", err?.response?.data?.message || "Gagal memuat data bulk update ODO.");
      }
    };

    loadBulkTemplateData();
    return () => {
      isCancelled = true;
      api.cancelPendingRequests({ group: REQ_GROUP_ODO_TD_BULK_TEMPLATE, reason: "Stop bulk template odo" });
    };
  }, [currentAction, panelForm?.bulk?.kodeCabang, panelForm?.bulk?.tanggal, bulkRefreshTick]);

  useEffect(() => {
    setBulkUploadState((prev) => ({ ...prev, validated: false }));
  }, [panelForm?.bulk?.tanggal, panelForm?.bulk?.kodeCabang]);

  const updatePanelField = (panelKey, field, value) => {
    setPanelForm((prev) => ({
      ...prev,
      [panelKey]: {
        ...prev[panelKey],
        [field]: value,
      },
    }));
    if (panelKey === "odo" || panelKey === "td") {
      setSubmitLocked((prev) => ({ ...prev, [panelKey]: false }));
    }
  };

  const collectInvalidCaptionIndexesOdo = (list = attachmentsOdo) => (
    (Array.isArray(list) ? list : [])
      .map((item, idx) => (String(item?.caption || "").trim() ? -1 : idx))
      .filter((idx) => idx >= 0)
  );

  const handleJenisTransaksiOdoChange = (value) => {
    const nextValue = String(value || "normal").trim();
    if (nextValue === "koreksi_odo") {
      setPanelForm((prev) => ({
        ...prev,
        odo: {
          ...prev.odo,
          jenisTransaksi: "koreksi_odo",
        },
      }));
      setSubmitLocked((prev) => ({ ...prev, odo: false }));
      return;
    }
    if (isOdoFlagTransaction(nextValue)) {
      setPanelForm((prev) => ({
        ...prev,
        odo: {
          ...prev.odo,
          jenisTransaksi: nextValue,
          alasanKoreksi: "",
        },
      }));
      setSubmitLocked((prev) => ({ ...prev, odo: false }));
      return;
    }
    setPanelForm((prev) => ({
      ...prev,
      odo: {
        ...prev.odo,
        jenisTransaksi: "normal",
        alasanKoreksi: "",
      },
    }));
    setSubmitLocked((prev) => ({ ...prev, odo: false }));
  };

  const handleSimpanUpdate = async (panelKey) => {
    if (panelKey === "odo") {
      const isKoreksiOdo = panelForm.odo.jenisTransaksi === "koreksi_odo";
      const odoInputForSubmit = (!isKoreksiOdo && Boolean(lastUpdate.odo.data?.hasKoreksiOdo))
        ? String(lastUpdate.odo.data?.odo || "")
        : panelForm.odo.odoInput;
      if (!isKoreksiOdo && Boolean(lastUpdate.odo.data?.hasKoreksiOdo)) {
        showNotif("error", "ODO hari ini sudah memiliki koreksi. Perubahan berikutnya harus melalui Koreksi ODO.");
        return;
      }
      if (!panelForm.odo.mt || !panelForm.odo.tanggal || !odoInputForSubmit) {
        showNotif("error", "Lengkapi MT, tanggal, dan nilai ODO.");
        return;
      }
      const odoBaru = Number(odoInputForSubmit);
      if (!Number.isFinite(odoBaru) || odoBaru <= 0) {
        showNotif("error", "Nilai ODO tidak valid.");
        return;
      }
      const lastOdo = Number(lastUpdate?.odo?.data?.odo || 0);
      if (!isKoreksiOdo && odoBaru < lastOdo) {
        showNotif("error", "ODO baru tidak boleh lebih kecil dari ODO sebelumnya.");
        return;
      }
      const lastTanggal = lastUpdate?.odo?.data?.tanggal;
      const gate = lastUpdate?.odo?.data?.odoGate || {};
      const gateOdo = Number.isFinite(Number(gate?.odo_baseline)) ? Number(gate.odo_baseline) : lastOdo;
      const spanDays = getDaySpanAtLeastOne(gate?.tanggal_baseline || lastTanggal, panelForm.odo.tanggal);
      const maxDelta = Number.isFinite(Number(gate?.max_delta)) ? Number(gate.max_delta) : spanDays * MAX_ODO_DELTA_PER_DAY;
      const deltaGate = odoBaru - gateOdo;
      if (!isKoreksiOdo && deltaGate > maxDelta) {
        showNotif(
          "error",
          `Selisih ODO melebihi batas ${formatRibuan(MAX_ODO_DELTA_PER_DAY)} km/hari (maks ${formatRibuan(maxDelta)} km untuk ${spanDays} hari).`
        );
        return;
      }
      if (isKoreksiOdo && !String(panelForm.odo.alasanKoreksi || "").trim()) {
        showNotif("error", "Alasan Koreksi wajib diisi.");
        return;
      }
      const invalidCaptionIndexes = collectInvalidCaptionIndexesOdo();
      if (invalidCaptionIndexes.length > 0) {
        setInvalidCaptionIndexesOdo(invalidCaptionIndexes);
        showNotif("error", "Caption dokumen wajib diisi untuk semua lampiran.");
        return;
      }
      setInvalidCaptionIndexesOdo([]);
    }
    if (panelKey === "td") {
      const tdPrepared = buildTdRowsForSubmit(panelForm.td.tdRows);
      if (tdPrepared.hasPartialRow) {
        showNotif("error", "Lengkapi 4 nilai TD pada baris yang sudah mulai diisi.");
        return;
      }
      if (tdPrepared.fullRows.length === 0) {
        showNotif("error", "Isi minimal 1 baris TD lengkap (TD1 sampai TD4).");
        return;
      }
      const tdRowsInput = tdPrepared.fullRows;

      const invalidRow = tdRowsInput.find((row) => {
        const prevTdRaw = tdLastRowByPosisi.get(String(row?.posisi || "").trim())?.td;
        const prev = parseTdParts(prevTdRaw);
        if (!prev) return true;
        const next = [
          Number(row.td1),
          Number(row.td2),
          Number(row.td3),
          Number(row.td4),
        ];
        if (next.some((x) => !Number.isFinite(x))) return true;
        if (row?.tdCorrection?.valid) return false;
        return next.some((x, idx) => x > prev[idx]);
      });

      if (invalidRow) {
        showNotif(
          "error",
          `TD posisi ${invalidRow.posisi} tidak boleh naik dari data terakhir.`
        );
        return;
      }
      const tdInvalidCaption = tdRowsInput.some((row) => (
        (Array.isArray(row?.temuanAttachments) ? row.temuanAttachments : [])
          .some((item) => item?.file instanceof File && !String(item?.caption || "").trim())
      ));
      if (tdInvalidCaption) {
        showNotif("error", "Caption dokumen wajib diisi untuk semua lampiran.");
        return;
      }
      const correctionWithoutMemo = tdRowsInput.find((row) => (
        row?.tdCorrection?.valid && !(row?.tdCorrection?.memo instanceof File)
      ));
      if (correctionWithoutMemo) {
        showNotif("error", `Upload Memo Koreksi TD posisi ${correctionWithoutMemo.posisi} wajib diisi.`);
        return;
      }
    }
    if (panelKey === "bulk") {
      if (!panelForm.bulk.file) {
        showNotif("error", "Pilih file Excel terlebih dahulu.");
        return;
      }
      if (!panelForm.bulk.kodeCabang || !panelForm.bulk.tanggal) {
        showNotif("error", "Pilih cabang dan tanggal terlebih dahulu.");
        return;
      }
      if (bulkUploadState.headerError) {
        showNotif("error", bulkUploadState.headerError);
        return;
      }
      if (!bulkHeaderDateMatches) {
        showNotif("error", "Tanggal pada header file tidak sama dengan tanggal input.");
        return;
      }
      if (bulkUploadState.invalid.length > 0) {
        showNotif("error", "Masih ada baris tidak valid pada file upload.");
        return;
      }
      if (bulkUploadState.valid.length === 0) {
        showNotif("error", "Tidak ada baris valid untuk diproses.");
        return;
      }
    }
    if (submitLocked[panelKey]) return;

    setSubmitLocked((prev) => ({ ...prev, [panelKey]: true }));
    try {
      if (panelKey === "odo") {
        api.cancelPendingRequests({ group: REQ_GROUP_ODO_TD_SUBMIT_ODO, reason: "Submit update odo terbaru" });
        const formData = new FormData();
        formData.append("id_mt", panelForm.odo.mt);
        formData.append("tanggal", panelForm.odo.tanggal);
        const odoInputForSubmit = (panelForm.odo.jenisTransaksi !== "koreksi_odo" && Boolean(lastUpdate.odo.data?.hasKoreksiOdo))
          ? String(lastUpdate.odo.data?.odo || "")
          : panelForm.odo.odoInput;
        formData.append("odo", String(Number(odoInputForSubmit)));
        formData.append("user", actorNama || "");
        formData.append("jenis_transaksi", panelForm.odo.jenisTransaksi || "normal");
        formData.append("alasan_koreksi", String(panelForm.odo.alasanKoreksi || "").trim());
        formData.append("catatan_flag", String(panelForm.odo.catatanFlag || "").trim());
        formData.append("nama_driver", String(panelForm.odo.namaDriver || "").trim());
        attachmentsOdo.forEach((item) => {
          if (item?.file instanceof File) formData.append("files", item.file);
          formData.append("captions", item?.caption || "");
        });
        await api.post(`${backendPort}/api/main/update_odo_mt`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          requestGroup: REQ_GROUP_ODO_TD_SUBMIT_ODO,
          timeout: 120000,
        });
        setPanelForm((prev) => ({
          ...prev,
          odo: {
            ...prev.odo,
            odoInput: "",
            namaDriver: "",
            alasanKoreksi: "",
            catatanFlag: "",
          },
        }));
        await loadLastUpdate("odo", panelForm.odo.mt);
        setAttachmentsOdo([]);
        setInvalidCaptionIndexesOdo([]);
                showNotif("success", "Update ODO berhasil disimpan.");
                if (typeof onAfterSave === "function") onAfterSave("odo");
                if (isModalMode && typeof onClose === "function") onClose();
      } else if (panelKey === "td") {
        const tdRowsInput = buildTdRowsForSubmit(panelForm.td.tdRows).fullRows;
        const tdFileRefs = [];
        const updates = tdRowsInput.map((row, rowIndex) => {
          const expectedIdBan = Number(tdLastUpdate?.banPositionMap?.[String(row?.posisi || "").trim()] || 0);
          const inspeksi = {};
          const jumlahBaut = String(row?.jumlahBaut || "").trim();
          const tekananSebelum = String(row?.tekananSebelum || "").trim();
          const tekananSesudah = String(row?.tekananSesudah || "").trim();
          const temuan = String(row?.temuan || "").trim();
          const temuanAttachments = Array.isArray(row?.temuanAttachments) ? row.temuanAttachments : [];

          if (jumlahBaut) inspeksi.baut = jumlahBaut;
          if (tekananSebelum) inspeksi.psi_0 = tekananSebelum;
          if (tekananSesudah) inspeksi.psi_1 = tekananSesudah;
          if (temuan) inspeksi.catatan = temuan;
          temuanAttachments.forEach((item) => {
            if (item?.file instanceof File) {
              tdFileRefs.push({
                update_index: rowIndex,
                kind: "inspeksi",
                caption: String(item?.caption || "").trim(),
              });
            }
          });
          if (row?.tdCorrection?.valid && row?.tdCorrection?.memo instanceof File) {
            tdFileRefs.push({
              update_index: rowIndex,
              kind: "koreksi_td_memo",
            });
          }
          if (temuanAttachments.some((item) => item?.file instanceof File)) inspeksi.lampiran = [];

          return {
            posisi: row.posisi,
            expected_id_ban: expectedIdBan,
            td: `${row.td1}-${row.td2}-${row.td3}-${row.td4}`,
            jenis_transaksi: row?.tdCorrection?.valid ? "koreksi_td" : "normal",
            alasan_koreksi: row?.tdCorrection?.valid ? String(row?.tdCorrection?.alasan || "").trim() : "",
            ...(Object.keys(inspeksi).length ? { inspeksi } : {}),
          };
        });
        const formData = new FormData();
        formData.append("id_mt", panelForm.td.mt);
        formData.append("tanggal", panelForm.td.tanggal);
        formData.append("updates", JSON.stringify(updates));
        formData.append("td_file_refs", JSON.stringify(tdFileRefs));
        formData.append("user", actorNama || "");
        tdRowsInput.forEach((row) => {
          (Array.isArray(row?.temuanAttachments) ? row.temuanAttachments : []).forEach((item) => {
            if (item?.file instanceof File) formData.append("files", item.file);
          });
          if (row?.tdCorrection?.valid && row?.tdCorrection?.memo instanceof File) {
            formData.append("files", row.tdCorrection.memo);
          }
        });
        api.cancelPendingRequests({ group: REQ_GROUP_ODO_TD_SUBMIT_TD, reason: "Submit update td terbaru" });
        await api.post(
          `${backendPort}/api/main/update_td_mt`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            requestGroup: REQ_GROUP_ODO_TD_SUBMIT_TD,
            timeout: 120000,
          }
        );
        await loadTdLastUpdate(panelForm.td.mt);
        showNotif("success", "Update data TD telah tersimpan");
        if (typeof onAfterSave === "function") onAfterSave("td");
        if (isModalMode && typeof onClose === "function") onClose();
        if (isPageModeTdAction) {
          setSubmitLocked((prev) => ({ ...prev, td: false }));
          setActiveAction("");
        }
      } else {
        const formData = new FormData();
        formData.append("kode_cabang", panelForm.bulk.kodeCabang);
        formData.append("tanggal", panelForm.bulk.tanggal);
        formData.append("user", actorNama);
        formData.append("file", panelForm.bulk.file);
        api.cancelPendingRequests({ group: REQ_GROUP_ODO_TD_SUBMIT_BULK, reason: "Submit bulk odo terbaru" });
        const { data } = await api.post(`${backendPort}/api/main/update_odo_mt_bulk_isolated`, formData, {
          requestGroup: REQ_GROUP_ODO_TD_SUBMIT_BULK,
          timeout: 120000,
        });
        const totalValid = Number(data?.data?.total_valid || bulkUploadState.valid.length || 0);
        const totalBan = Number(data?.data?.total_ban_updated || 0);
        showNotif("success", `Upload bulk berhasil: ${totalValid} MT diproses, ${totalBan} baris ban_kmtd ter-update.`);
        setPanelForm((prev) => ({
          ...prev,
          bulk: { ...prev.bulk, file: null },
        }));
        if (bulkFileInputRef.current) {
          bulkFileInputRef.current.value = "";
        }
        setBulkUploadState({
          parsing: false,
          valid: [],
          invalid: [],
          totalReadRows: 0,
          kmHeader: "",
          headerDate: "",
          headerError: "",
          validated: false,
        });
        setBulkRefreshTick((x) => x + 1);
        if (typeof onAfterSave === "function") onAfterSave("bulk");
      }
    } catch (err) {
      if (api.isCanceledError(err)) {
        setSubmitLocked((prev) => ({ ...prev, [panelKey]: false }));
        return;
      }
      console.error(err);
      setSubmitLocked((prev) => ({ ...prev, [panelKey]: false }));
      const fallbackMessage = panelKey === "odo" && panelForm?.odo?.jenisTransaksi === "koreksi_odo"
        ? "Koreksi ODO Gagal karena km tempuh tidak boleh minus."
        : "Gagal menyimpan data.";
      showNotif("error", err?.response?.data?.message || fallbackMessage);
    }
  };

  const renderCabangField = (panelKey, extraClassName = "") => {
    const value = panelForm[panelKey].kodeCabang;
    if (!isPusat) {
      return <input className={`form-control ${extraClassName}`.trim()} value={namaCabangUser || ""} readOnly />;
    }
    return (
      <select
        className={`form-select ${extraClassName}`.trim()}
        value={value}
        onChange={(e) => {
          updatePanelField(panelKey, "kodeCabang", e.target.value);
          if (panelKey === "odo" || panelKey === "td") {
            updatePanelField(panelKey, "mt", "");
          }
        }}
      >
        <option value="">-Pilih Cabang-</option>
        {opsiCabang.map((x) => (
          <option key={x.kode_cabang} value={x.kode_cabang}>
            {x.nama_cabang}
          </option>
        ))}
      </select>
    );
  };

  const selectedBulkCabangNama = useMemo(() => {
    const kodeCabang = String(panelForm?.bulk?.kodeCabang || "").trim();
    if (!kodeCabang) return "-";
    if (!isPusat) return String(namaCabangUser || "-");
    return String(opsiCabang.find((x) => String(x?.kode_cabang || "").trim() === kodeCabang)?.nama_cabang || "-");
  }, [namaCabangUser, isPusat, opsiCabang, panelForm?.bulk?.kodeCabang]);
  const bulkMtMetaById = useMemo(() => {
    const entries = Array.isArray(bulkTemplateState?.rows) ? bulkTemplateState.rows : [];
    const out = {};
    entries.forEach((row) => {
      const idMt = String(row?.id_mt || "").trim();
      if (!idMt) return;
      out[idMt] = {
        nopol: String(row?.nopol || "").trim(),
        roda: Number(row?.jumlah_roda || 0),
        daftar_ban: parseDaftarBan(row?.daftar_ban),
        hasKoreksiOdoHariIni: Boolean(Number(row?.has_koreksi_odo_hari_ini || 0))
          || (
            String(row?.tanggal_update_odo || "").slice(0, 10) === String(panelForm?.bulk?.tanggal || "").trim()
            && hasReffHistory(row?.reff, "koreksi_odo")
          ),
      };
    });
    return out;
  }, [bulkTemplateState?.rows, panelForm?.bulk?.tanggal]);
  const countBulkPosisiPActive = (daftarBanRaw) => (
    Object.entries(parseDaftarBan(daftarBanRaw))
      .filter(([posisi, idBan]) => (
        String(posisi || "").trim().toUpperCase().startsWith("P-")
        && Number(idBan) > 0
      ))
      .length
  );
  const bulkHeaderDateMatches = useMemo(
    () => String(bulkUploadState.headerDate || "").trim() === String(panelForm?.bulk?.tanggal || "").trim(),
    [bulkUploadState.headerDate, panelForm?.bulk?.tanggal]
  );
  const setActiveTdPosisi = (posisi) => {
    const targetPosisi = String(posisi || "").trim();
    if (!targetPosisi) return;
    setPanelForm((prev) => {
      const idx = prev.td.activeRowIndex;
      const activeRow = prev.td.tdRows[idx] || null;
      if (String(activeRow?.posisi || "") === targetPosisi) {
        return {
          ...prev,
          td: {
            ...prev.td,
            activePosisi: targetPosisi,
            activeRowIndex: idx,
          },
        };
      }
      const duplicateIndex = prev.td.tdRows.findIndex(
        (row, i) => i !== idx && String(row?.posisi || "") === targetPosisi
      );
      if (duplicateIndex >= 0) {
        return {
          ...prev,
          td: {
            ...prev.td,
            activePosisi: targetPosisi,
            activeRowIndex: duplicateIndex,
          },
        };
      }
      if (activeRow?.tdCorrection?.valid) {
        const newRows = [
          ...prev.td.tdRows,
          createEmptyTdRow(targetPosisi),
        ];
        return {
          ...prev,
          td: {
            ...prev.td,
            activePosisi: targetPosisi,
            activeRowIndex: newRows.length - 1,
            tdRows: newRows,
          },
        };
      }
      const updatedRows = prev.td.tdRows.map((row, i) => (i === idx ? { ...row, posisi: targetPosisi } : row));
      return {
        ...prev,
        td: {
          ...prev.td,
          activePosisi: targetPosisi,
          tdRows: updatedRows,
        },
      };
    });
    setSubmitLocked((prev) => (prev.td ? { ...prev, td: false } : prev));
    //if (duplicateIndex >= 0) {
    //  showNotif("info", `Posisi ${posisi} sudah ada di baris lain, fokus dipindah ke baris tersebut.`);
    //}
  };

  const updateTdRow = (index, field, value) => {
    setPanelForm((prev) => {
      const updatedRows = prev.td.tdRows.map((row, i) => (i === index ? { ...row, [field]: value } : row));
      const updatedRowsWithFlag = updatedRows.map((row, i) => (
        i === index ? { ...row, isEdited: true } : row
      ));
      return {
        ...prev,
        td: {
          ...prev.td,
          tdRows: updatedRowsWithFlag,
        },
      };
    });
    setSubmitLocked((prev) => (prev.td ? { ...prev, td: false } : prev));
  };

  const getTdInputValueByPosisi = (posisi, field) => {
    const found = tdInputRowByPosisi.get(String(posisi || "").trim());
    if (!found?.tdCorrection?.valid && isTdNormalLockedByCorrection(posisi)) {
      const prevParts = getLastTdPartsByPosisi(posisi);
      const fieldIndexMap = { td1: 0, td2: 1, td3: 2, td4: 3 };
      const idx = fieldIndexMap[field];
      return idx == null || !prevParts ? "" : String(prevParts[idx] ?? "");
    }
    const directValue = String(found?.[field] ?? "");
    if (found?.touchedFields?.[field]) return directValue;
    if (directValue !== "") return directValue;
    return getTdCadanganDefaultValue(posisi, field);
  };

  const getTdInputRowByPosisi = (posisi) => (
    tdInputRowByPosisi.get(String(posisi || "").trim()) || null
  );

  const getTdAuxInputValueByPosisi = (posisi, field) => {
    const found = getTdInputRowByPosisi(posisi);
    return String(found?.[field] ?? "");
  };

  const updateTdAuxRowByPosisi = (posisi, field, rawValue) => {
    const targetPosisi = String(posisi || "").trim();
    if (!targetPosisi) return;
    if (isTdCorrectionLocked(targetPosisi)) return;
    if (isTdNormalLockedByCorrection(targetPosisi)) return;
    const value = field === "temuan"
      ? String(rawValue ?? "")
      : String(rawValue ?? "").replace(/[^\d]/g, "");
    setPanelForm((prev) => {
      const existingIndex = prev.td.tdRows.findIndex(
        (row) => String(row?.posisi || "") === targetPosisi
      );
      if (existingIndex >= 0) {
        const updatedRows = prev.td.tdRows.map((row, i) => (
          i === existingIndex
            ? { ...row, posisi: targetPosisi, [field]: value, isEdited: true }
            : row
        ));
        return {
          ...prev,
          td: {
            ...prev.td,
            activePosisi: targetPosisi,
            activeRowIndex: existingIndex,
            tdRows: updatedRows,
          },
        };
      }
      const newRows = [
        ...prev.td.tdRows,
        { ...createEmptyTdRow(targetPosisi), [field]: value, isEdited: true },
      ];
      return {
        ...prev,
        td: {
          ...prev.td,
          activePosisi: targetPosisi,
          activeRowIndex: newRows.length - 1,
          tdRows: newRows,
        },
      };
    });
    setSubmitLocked((prev) => (prev.td ? { ...prev, td: false } : prev));
  };

  const closeTdTemuanModal = () => {
    setTdTemuanModal({
      open: false,
      posisi: "",
      temuan: "",
      attachments: [],
    });
  };

  const closeTdLastTemuanModal = () => {
    setTdLastTemuanPreviewOpen(false);
    setTdLastTemuanModal({
      open: false,
      posisi: "",
      temuan: "",
      files: [],
    });
  };

  const openTdLastTemuanModal = (row) => {
    const targetPosisi = String(row?.posisi || "").trim();
    if (!targetPosisi) return;
    const temuan = String(row?.lastTemuan || "").trim();
    const files = Array.isArray(row?.lastTemuanFiles) ? row.lastTemuanFiles : [];
    if (!temuan && files.length <= 0) return;
    setActiveTdPosisi(targetPosisi);
    setTdLastTemuanPreviewOpen(false);
    setTdLastTemuanModal({
      open: true,
      posisi: targetPosisi,
      temuan,
      files,
    });
  };

  const openTdTemuanModal = (posisi) => {
    const targetPosisi = String(posisi || "").trim();
    if (!targetPosisi) return;
    const row = getTdInputRowByPosisi(targetPosisi);
    setActiveTdPosisi(targetPosisi);
    setTdTemuanModal({
      open: true,
      posisi: targetPosisi,
      temuan: String(row?.temuan || ""),
      attachments: Array.isArray(row?.temuanAttachments) ? row.temuanAttachments : [],
    });
  };

  const getTdLastTemuanTextByPosisi = (posisi) => {
    const targetPosisi = String(posisi || "").trim();
    if (!targetPosisi) return "";
    return String(tdLastRowByPosisi.get(targetPosisi)?.lastTemuan || "").trim();
  };

  const toggleTdTemuanFromLastCheck = () => {
    setTdTemuanModal((prev) => {
      const currentTemuan = String(prev?.temuan || "").trim();
      if (currentTemuan) return { ...prev, temuan: "" };
      return { ...prev, temuan: getTdLastTemuanTextByPosisi(prev?.posisi) };
    });
  };

  const updateTdTemuanAttachments = (updater) => {
    setTdTemuanModal((prev) => {
      const current = Array.isArray(prev.attachments) ? prev.attachments : [];
      const nextAttachments = typeof updater === "function" ? updater(current) : updater;
      return {
        ...prev,
        attachments: Array.isArray(nextAttachments) ? nextAttachments : [],
      };
    });
  };

  const isTdTemuanDraftValid = (draft = tdTemuanModal) => (
    Boolean(String(draft?.temuan || "").trim())
  );

  const applyTdTemuanModal = () => {
    if (!isTdTemuanDraftValid()) {
      showNotif("error", "Temuan wajib diisi.");
      return;
    }
    const targetPosisi = String(tdTemuanModal.posisi || "").trim();
    if (!targetPosisi) return;
    const normalizedTemuan = String(tdTemuanModal.temuan || "").trim();
    const normalizedAttachments = (Array.isArray(tdTemuanModal.attachments) ? tdTemuanModal.attachments : [])
      .filter((item) => item?.file || item?.preview || String(item?.caption || "").trim());
    setPanelForm((prev) => {
      const existingIndex = prev.td.tdRows.findIndex(
        (row) => String(row?.posisi || "") === targetPosisi
      );
      const temuanPatch = {
        posisi: targetPosisi,
        temuan: normalizedTemuan,
        temuanAttachments: normalizedAttachments,
        isEdited: true,
      };
      const nextRows = existingIndex >= 0
        ? prev.td.tdRows.map((row, idx) => (idx === existingIndex ? { ...row, ...temuanPatch } : row))
        : [...prev.td.tdRows, { ...createEmptyTdRow(targetPosisi), ...temuanPatch }];
      return {
        ...prev,
        td: {
          ...prev.td,
          activePosisi: targetPosisi,
          activeRowIndex: existingIndex >= 0 ? existingIndex : nextRows.length - 1,
          tdRows: nextRows,
        },
      };
    });
    closeTdTemuanModal();
    setSubmitLocked((prev) => (prev.td ? { ...prev, td: false } : prev));
  };

  const isTdCorrectionLocked = (posisi) => Boolean(getTdInputRowByPosisi(posisi)?.tdCorrection?.valid);

  const getTdLastRowByPosisi = (posisi) => (
    tdLastRowByPosisi.get(String(posisi || "").trim()) || null
  );

  const isTdNormalLockedByCorrection = (posisi) => {
    const row = getTdLastRowByPosisi(posisi);
    if (!row || !isTodayDate(row?.tanggal)) return false;
    return hasReffHistory(row?.reff, "koreksi_td") || hasReffHistory(row?.reff, "koreksi_odo");
  };

  const showTdBanTooltip = ({ pos = "", id_ban = 0, x = 0, y = 0 } = {}) => {
    const posisi = String(pos || "").trim();
    if (!posisi) return;
    setTdBanTooltip({
      visible: true,
      x: Number(x || 0),
      y: Number(y || 0),
      posisi,
      id_ban: Number(id_ban || 0),
    });
  };

  const hideTdBanTooltip = () => {
    setTdBanTooltip((prev) => ({ ...prev, visible: false }));
  };

  useEffect(() => {
    const onMouseMove = (event) => {
      if (!tdBanTooltip?.visible) return;
      const hit = event?.target;
      const inBanHover = Boolean(hit?.closest?.('[data-ban-hover="1"]'));
      const inFormasiWrap = Boolean(hit?.closest?.(".page25-td-formasi-wrap"));
      if (!inBanHover && !inFormasiWrap) hideTdBanTooltip();
    };
    window.addEventListener("mousemove", onMouseMove);
    return () => {
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, [tdBanTooltip?.visible]);

  const openTdFormasiPopup = () => {
    setTdFormasiPopup((prev) => {
      if (prev.open) return { ...prev, open: false, dragging: false };
      const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
      return {
        ...prev,
        open: true,
        x: Math.max(16, viewportWidth - 376),
        y: 96,
        dragging: false,
      };
    });
  };

  const startTdFormasiDrag = (event) => {
    const clientX = event?.clientX ?? event?.touches?.[0]?.clientX;
    const clientY = event?.clientY ?? event?.touches?.[0]?.clientY;
    if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
    setTdFormasiPopup((prev) => ({
      ...prev,
      dragging: true,
      dragOffsetX: clientX - Number(prev.x || 0),
      dragOffsetY: clientY - Number(prev.y || 0),
    }));
  };

  useEffect(() => {
    if (!tdFormasiPopup.dragging) return undefined;
    const movePopup = (event) => {
      const clientX = event?.clientX ?? event?.touches?.[0]?.clientX;
      const clientY = event?.clientY ?? event?.touches?.[0]?.clientY;
      if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) return;
      if (event?.cancelable) event.preventDefault();
      setTdFormasiPopup((prev) => {
        const viewportWidth = typeof window !== "undefined" ? window.innerWidth : 1280;
        const viewportHeight = typeof window !== "undefined" ? window.innerHeight : 720;
        return {
          ...prev,
          x: Math.min(Math.max(8, clientX - Number(prev.dragOffsetX || 0)), Math.max(8, viewportWidth - 120)),
          y: Math.min(Math.max(72, clientY - Number(prev.dragOffsetY || 0)), Math.max(72, viewportHeight - 96)),
        };
      });
    };
    const stopDrag = () => {
      setTdFormasiPopup((prev) => ({ ...prev, dragging: false }));
    };
    window.addEventListener("mousemove", movePopup);
    window.addEventListener("mouseup", stopDrag);
    window.addEventListener("touchmove", movePopup, { passive: false });
    window.addEventListener("touchend", stopDrag);
    return () => {
      window.removeEventListener("mousemove", movePopup);
      window.removeEventListener("mouseup", stopDrag);
      window.removeEventListener("touchmove", movePopup);
      window.removeEventListener("touchend", stopDrag);
    };
  }, [tdFormasiPopup.dragging]);

  const tdBanTooltipData = (() => {
    if (!tdBanTooltip.visible) return null;
    const posisi = String(tdBanTooltip.posisi || "").trim();
    if (!posisi) return null;
    const row = getTdLastRowByPosisi(posisi);
    if (!row) return null;
    return {
      posisi,
      seri: String(row?.seri || "-").trim() || "-",
      td: String(row?.td || "-").trim() || "-",
      kmt: formatKmTooltip(row?.kmt),
    };
  })();

  const getLastTdPartsByPosisi = (posisi) => {
    const prevRow = tdLastRowByPosisi.get(String(posisi || "").trim());
    return parseTdParts(prevRow?.td);
  };

  const isTdInputAllowed = (posisi, field, rawValue) => {
    const value = String(rawValue ?? "").trim();
    if (value === "") return true;
    if (!/^\d+$/.test(value)) return false;

    const prevRow = tdLastRowByPosisi.get(String(posisi || "").trim());
    const prevParts = parseTdParts(prevRow?.td);
    if (!prevParts) return true;

    const fieldIndexMap = { td1: 0, td2: 1, td3: 2, td4: 3 };
    const idx = fieldIndexMap[field];
    if (idx == null) return false;

    const prevValue = Number(prevParts[idx]);
    const nextValue = Number(value);
    if (!Number.isFinite(nextValue)) return false;
    if (!Number.isFinite(prevValue)) return true;
    return nextValue <= prevValue;
  };

  const updateTdRowByPosisi = (posisi, field, value) => {
    const targetPosisi = String(posisi || "").trim();
    if (!targetPosisi) return;
    if (isTdCorrectionLocked(targetPosisi)) return;
    if (isTdNormalLockedByCorrection(targetPosisi)) return;
    if (!isTdInputAllowed(targetPosisi, field, value)) return;
    setPanelForm((prev) => {
      const existingIndex = prev.td.tdRows.findIndex(
        (row) => String(row?.posisi || "") === targetPosisi
      );
      if (existingIndex >= 0) {
        const updatedRows = prev.td.tdRows.map((row, i) => (
          i === existingIndex
            ? {
                ...row,
                posisi: targetPosisi,
                [field]: value,
                touchedFields: { ...(row?.touchedFields || {}), [field]: true },
                isEdited: true,
              }
            : row
        ));
        return {
          ...prev,
          td: {
            ...prev.td,
            activePosisi: targetPosisi,
            activeRowIndex: existingIndex,
            tdRows: updatedRows,
          },
        };
      }
      const newRows = [
        ...prev.td.tdRows,
        {
          ...createEmptyTdRow(targetPosisi),
          [field]: value,
          touchedFields: { [field]: true },
          isEdited: true,
        },
      ];
      return {
        ...prev,
        td: {
          ...prev.td,
          activePosisi: targetPosisi,
          activeRowIndex: newRows.length - 1,
          tdRows: newRows,
        },
      };
    });
    setSubmitLocked((prev) => (prev.td ? { ...prev, td: false } : prev));
  };

  const isTdCorrectionDraftValid = (draft = tdCorrectionModal) => {
    const posisi = String(draft?.posisi || "").trim();
    if (!posisi) return false;
    if (!String(draft?.alasan || "").trim()) return false;
    if (!(draft?.memo instanceof File)) return false;
    const fields = ["td1", "td2", "td3", "td4"];
    return fields.every((field) => {
      const value = String(draft?.[field] ?? "").trim();
      if (!/^\d+$/.test(value)) return false;
      const nextValue = Number(value);
      return Number.isFinite(nextValue) && nextValue <= 16;
    });
  };

  const openTdCorrectionModal = (posisi) => {
    const targetPosisi = String(posisi || "").trim();
    if (!targetPosisi) return;
    const row = getTdInputRowByPosisi(targetPosisi);
    setActiveTdPosisi(targetPosisi);
    setTdCorrectionModal({
      open: true,
      posisi: targetPosisi,
      alasan: String(row?.tdCorrection?.alasan || ""),
      td1: String(row?.td1 ?? ""),
      td2: String(row?.td2 ?? ""),
      td3: String(row?.td3 ?? ""),
      td4: String(row?.td4 ?? ""),
      memo: row?.tdCorrection?.memo instanceof File ? row.tdCorrection.memo : null,
    });
  };

  const updateTdCorrectionDraft = (field, value) => {
    const nextValue = field === "alasan" ? value : String(value ?? "").replace(/[^\d]/g, "");
    setTdCorrectionModal((prev) => ({ ...prev, [field]: nextValue }));
  };

  const updateTdCorrectionMemo = (fileInput) => {
    const file = fileInput instanceof File ? fileInput : null;
    if (!file) {
      setTdCorrectionModal((prev) => ({ ...prev, memo: null }));
      return;
    }
    if (!TD_CORRECTION_MEMO_TYPES.has(String(file.type || "").toLowerCase())) {
      setTdCorrectionModal((prev) => ({ ...prev, memo: null }));
      showNotif("error", "Memo harus berupa JPG, PNG, WebP, atau PDF.");
      return;
    }
    if (file.size > MAX_TD_CORRECTION_MEMO_SIZE) {
      setTdCorrectionModal((prev) => ({ ...prev, memo: null }));
      showNotif("error", "Ukuran Memo maksimal 5 MB.");
      return;
    }
    setTdCorrectionModal((prev) => ({ ...prev, memo: file }));
  };

  const clearTdCorrectionForPosisi = (posisi = tdCorrectionModal.posisi) => {
    const targetPosisi = String(posisi || "").trim();
    if (!targetPosisi) return;
    setPanelForm((prev) => {
      const existingIndex = prev.td.tdRows.findIndex((row) => String(row?.posisi || "") === targetPosisi);
      const nextRows = existingIndex >= 0
        ? prev.td.tdRows.map((row, idx) => (
            idx === existingIndex
              ? {
                  ...row,
                  td1: "",
                  td2: "",
                  td3: "",
                  td4: "",
                  touchedFields: {},
                  tdCorrection: null,
                  isEdited: false,
                }
              : row
          ))
        : prev.td.tdRows;
      return {
        ...prev,
        td: {
          ...prev.td,
          tdRows: nextRows,
        },
      };
    });
    setTdCorrectionModal((prev) => ({
      ...prev,
      alasan: "",
      td1: "",
      td2: "",
      td3: "",
      td4: "",
      memo: null,
    }));
    setSubmitLocked((prev) => ({ ...prev, td: false }));
  };

  const applyTdCorrectionModal = () => {
    if (!isTdCorrectionDraftValid()) {
      showNotif("error", "Lengkapi alasan koreksi, 4 nilai TD yang valid, dan Upload Memo.");
      return;
    }
    const targetPosisi = String(tdCorrectionModal.posisi || "").trim();
    setPanelForm((prev) => {
      const existingIndex = prev.td.tdRows.findIndex((row) => String(row?.posisi || "") === targetPosisi);
      const correctionPatch = {
        posisi: targetPosisi,
        td1: String(tdCorrectionModal.td1 || "").trim(),
        td2: String(tdCorrectionModal.td2 || "").trim(),
        td3: String(tdCorrectionModal.td3 || "").trim(),
        td4: String(tdCorrectionModal.td4 || "").trim(),
        touchedFields: { td1: true, td2: true, td3: true, td4: true },
        tdCorrection: {
          valid: true,
          alasan: String(tdCorrectionModal.alasan || "").trim(),
          memo: tdCorrectionModal.memo,
        },
        isEdited: true,
      };
      const nextRows = existingIndex >= 0
        ? prev.td.tdRows.map((row, idx) => (idx === existingIndex ? { ...row, ...correctionPatch } : row))
        : [...prev.td.tdRows, correctionPatch];
      return {
        ...prev,
        td: {
          ...prev.td,
          activePosisi: targetPosisi,
          activeRowIndex: existingIndex >= 0 ? existingIndex : nextRows.length - 1,
          tdRows: nextRows,
        },
      };
    });
    setTdCorrectionModal({
      open: false,
      posisi: "",
      alasan: "",
      td1: "",
      td2: "",
      td3: "",
      td4: "",
      memo: null,
    });
    setSubmitLocked((prev) => ({ ...prev, td: false }));
  };

  const fillTdRowsWithLastValue = () => {
    const nextRows = tdLastUpdate.rows.map((row) => {
      const prevParts = parseTdParts(row?.td);
      if (!prevParts) {
        return createEmptyTdRow(row?.posisi || "");
      }
      return {
        ...createEmptyTdRow(row?.posisi || ""),
        td1: String(prevParts[0]),
        td2: String(prevParts[1]),
        td3: String(prevParts[2]),
        td4: String(prevParts[3]),
        isEdited: true,
      };
    });
    const invalidCount = nextRows.filter((row) => !row.isEdited).length;
    if (invalidCount > 0) {
      showNotif(
        "info",
        `${invalidCount} posisi belum bisa diisi otomatis karena format Last TD tidak lengkap.`
      );
    }
    return nextRows.length
      ? nextRows
      : [createEmptyTdRow()];
  };

  const handleTdNoSignificantChangeToggle = (checked) => {
    setPanelForm((prev) => {
      const firstPosisi = tdLastUpdate.rows[0]?.posisi || prev.td.activePosisi || "";
      return {
        ...prev,
        td: {
          ...prev.td,
          tdNoSignificantChange: checked,
          activePosisi: firstPosisi,
          activeRowIndex: 0,
          tdRows: checked
            ? fillTdRowsWithLastValue()
            : [createEmptyTdRow(firstPosisi)],
        },
      };
    });
    setSubmitLocked((prev) => ({ ...prev, td: false }));
  };

  const addTdRow = () => {
    setPanelForm((prev) => {
      const usedPosisi = new Set(prev.td.tdRows.map((row) => row.posisi).filter(Boolean));
      const allPosisi = tdLastUpdate.rows.map((row) => row.posisi);
      const nextPosisi = allPosisi.find((p) => !usedPosisi.has(p)) || "";
      const newRows = [...prev.td.tdRows, createEmptyTdRow(nextPosisi)];
      return {
        ...prev,
        td: {
          ...prev.td,
          activePosisi: nextPosisi || prev.td.activePosisi,
          tdRows: newRows,
          activeRowIndex: newRows.length - 1,
        },
      };
    });
    setSubmitLocked((prev) => ({ ...prev, td: false }));
  };

  const removeTdRow = () => {
    setPanelForm((prev) => {
      if (prev.td.tdRows.length <= 1) return prev;
      const newRows = prev.td.tdRows.slice(0, -1);
      const newActiveRowIndex = Math.min(prev.td.activeRowIndex, newRows.length - 1);
      return {
        ...prev,
        td: {
          ...prev.td,
          tdRows: newRows,
          activeRowIndex: newActiveRowIndex,
          activePosisi: newRows[newActiveRowIndex]?.posisi || "",
        },
      };
    });
    setSubmitLocked((prev) => ({ ...prev, td: false }));
  };

  const tdPreparedRows = useMemo(
    () => buildTdRowsForSubmit(panelForm.td.tdRows),
    [panelForm.td.tdRows, tdLastUpdate.rows]
  );
  const tdCanSubmit = tdPreparedRows.fullRows.length > 0 && !tdPreparedRows.hasPartialRow;
  const odoInvalidCabang = currentAction === "odo" && !String(panelForm?.odo?.kodeCabang || "").trim();
  const odoInvalidMt = currentAction === "odo" && !String(panelForm?.odo?.mt || "").trim();
  const odoInvalidInput = currentAction === "odo"
    && !(panelForm.odo.jenisTransaksi !== "koreksi_odo" && Boolean(lastUpdate.odo.data?.hasKoreksiOdo))
    && !String(panelForm?.odo?.odoInput || "").trim();
  const odoInvalidAlasanKoreksi = currentAction === "odo"
    && panelForm?.odo?.jenisTransaksi === "koreksi_odo"
    && !String(panelForm?.odo?.alasanKoreksi || "").trim();
  const tdInvalidCabang = currentAction === "td" && !String(panelForm?.td?.kodeCabang || "").trim();
  const tdInvalidMt = currentAction === "td" && !String(panelForm?.td?.mt || "").trim();
  const tdRequiredInvalidKeys = useMemo(() => {
    const invalidKeys = new Set();
    if (currentAction !== "td" || !panelForm?.td?.mt || tdCanSubmit) return invalidKeys;
    const fields = ["td1", "td2", "td3", "td4"];
    const rowsToCheck = tdPreparedRows.rowsWithAnyValue.length
      ? tdPreparedRows.rowsWithAnyValue
      : [{ posisi: panelForm.td.activePosisi || tdLastUpdate.rows[0]?.posisi || "" }];
    rowsToCheck.forEach((row) => {
      const posisi = String(row?.posisi || "").trim();
      if (!posisi) return;
      fields.forEach((field) => {
        if (String(row?.[field] ?? "").trim() === "") invalidKeys.add(`${posisi}:${field}`);
      });
    });
    return invalidKeys;
  }, [currentAction, panelForm?.td?.mt, panelForm?.td?.activePosisi, tdCanSubmit, tdPreparedRows, tdLastUpdate.rows]);
  const getTdRequiredInvalidClass = (posisi, field) => (
    tdRequiredInvalidKeys.has(`${String(posisi || "").trim()}:${field}`) ? "field-required-invalid" : ""
  );
  const bulkInvalidCabang = currentAction === "bulk" && !String(panelForm?.bulk?.kodeCabang || "").trim();
  const bulkInvalidFile = currentAction === "bulk" && !panelForm?.bulk?.file;
  const odoNormalLockedByCorrection = currentAction === "odo"
    && panelForm.odo.jenisTransaksi !== "koreksi_odo"
    && Boolean(lastUpdate.odo.data?.hasKoreksiOdo);
  const odoInputDisplayValue = odoNormalLockedByCorrection
    ? String(lastUpdate.odo.data?.odo || "")
    : panelForm.odo.odoInput;

  return (
    <div className="container-fluid px-0 page16-wo-elegant-theme page25-update-theme page16-wo2-table-theme">
      {tdBanTooltip.visible && tdBanTooltipData && (
        <div
          style={{
            position: "fixed",
            ...getTooltipPositionStyle(tdBanTooltip.x, tdBanTooltip.y),
            zIndex: 9998,
            pointerEvents: "none",
            minWidth: "220px",
            maxWidth: "280px",
            padding: "10px 12px",
            borderRadius: "10px",
            border: "1px solid #0f172a",
            background: "rgba(15, 23, 42, 0.96)",
            color: "#e2e8f0",
            boxShadow: "0 10px 24px rgba(2,6,23,0.35)",
            fontSize: "0.78rem",
            lineHeight: 1.45,
          }}
        >
          <div className="d-flex justify-content-between gap-2">
            <span style={{ color: "#93c5fd" }}>Posisi</span>
            <span style={{ fontWeight: 700, textAlign: "right" }}>{tdBanTooltipData.posisi || "-"}</span>
          </div>
          <div className="d-flex justify-content-between gap-2">
            <span style={{ color: "#93c5fd" }}>No Seri</span>
            <span style={{ fontWeight: 700, textAlign: "right" }}>{tdBanTooltipData.seri || "-"}</span>
          </div>
          <div className="d-flex justify-content-between gap-2">
            <span style={{ color: "#93c5fd" }}>Last TD</span>
            <span style={{ fontWeight: 600, textAlign: "right" }}>{tdBanTooltipData.td || "-"}</span>
          </div>
          <div className="d-flex justify-content-between gap-2">
            <span style={{ color: "#93c5fd" }}>KMT</span>
            <span style={{ fontWeight: 600, textAlign: "right" }}>{tdBanTooltipData.kmt || "-"}</span>
          </div>
        </div>
      )}
      <div className="page16-header d-flex justify-content-between align-items-start flex-wrap gap-2 mb-4">
        <div>
          <h3 className="page16-title">Update ODO dan TD</h3>
        </div>
      </div>

      <div className="row g-4">
        {!forcedAction && !isPageModeTdExpanded && (
          <div className="col-12 col-md-4">
            <div className="page16-action-grid">
              <div className="row g-2">
                {ODO_TD_ACTIONS.map((action) => (
                  <div key={action.value} className="col-12">
                    <button
                      type="button"
                      className={`page15-odotd-action ${activeAction === action.value ? "is-active" : ""}`}
                      onClick={() => setActiveAction(action.value)}
                    >
                      <div className="d-flex justify-content-between align-items-start gap-2">
                        <div>
                          <h5 className="mb-1 text-white">{action.title}</h5>
                          <p className="mb-0 text-white-80 small">{action.description}</p>
                        </div>
                        <span className="page16-action-state text-white">
                          {activeAction === action.value ? "Aktif" : "Pilih"}
                        </span>
                      </div>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className={forcedAction || isPageModeTdExpanded ? "col-12" : "col-12 col-md-8"}>
          <div
            className={`${selectedAction ? "page16-panel-form h-100" : ""}  ${panelAccentClassByUpdateAction(currentAction)} ${isModalMode ? "page25-modal-panel-accent" : ""}`}
          >
            {!selectedAction && (
              <div className="page16-panel-placeholder d-flex flex-column justify-content-center align-items-center text-center">
                <p className="fw-semibold mb-1">Belum ada action dipilih.</p>
                <p className="text-muted small mb-0">Klik salah satu action di sebelah kiri untuk mulai update.</p>
              </div>
            )}

            {selectedAction && (
              <div>
                <div className="d-flex justify-content-between align-items-start mb-3 border-bottom pb-3">
                  <div>
                    <p className="page16-panel-label mb-1">Detail Aktivitas</p>
                    <h4 className="page16-panel-title mb-1">{selectedAction.title}</h4>
                    <p className="text-muted small mb-0">{selectedAction.description}</p>
                  </div>
                  {!forcedAction ? (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary page16-panel-close"
                      onClick={() => setActiveAction("")}
                      aria-label="Tutup panel"
                    >
                      <i className="fa fa-times" aria-hidden="true" />
                    </button>
                  ) : (
                    isModalMode && typeof onClose === "function" && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary page16-panel-close"
                        onClick={onClose}
                        aria-label="Tutup modal"
                      >
                        <i className="fa fa-times" aria-hidden="true" />
                      </button>
                    )
                  )}
                </div>

                {(currentAction === "odo" || currentAction === "td") && (
                  <div className="row g-3 mb-3">
                    <div className="col-12 col-lg-4">
                      <label className="form-label">Nama Cabang</label>
                      {renderCabangField(currentAction, (odoInvalidCabang || tdInvalidCabang) ? "field-required-invalid" : "")}
                    </div>
                    <div className="col-12 col-lg-4">
                      <label className="form-label">Tanggal</label>
                      <input
                        type="date"
                        className="form-control"
                        value={panelForm[currentAction].tanggal}
                        readOnly
                      />
                    </div>
                    <div className="col-12 col-lg-4">
                      <label className="form-label">No. Pol</label>
                      <input
                        className={`form-control ${odoInvalidMt || tdInvalidMt ? "field-required-invalid" : ""}`}
                        value={selectedCurrentMt?.nopol || ""}
                        placeholder="-Pilih NoPol MT-"
                        readOnly
                        style={{ cursor: isMtLockedFromParent ? "default" : "pointer" }}
                        onClick={() => {
                          if (isMtLockedFromParent) return;
                          if (!panelForm[currentAction].kodeCabang) return;
                          setMtPickerTarget(currentAction);
                          setShowCariMtModal(true);
                        }}
                      />
                      <small className="text-muted">
                        {selectedCurrentMt?.id_mt ? `ID: ${selectedCurrentMt.id_mt}` : "Belum ada MT dipilih"}
                      </small>
                    </div>
                  </div>
                )}

                {currentAction === "odo" && panelForm.odo.mt && (
                  <div className="card page16-info-card mt-1">
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="page16-info-title">Last Update</span>
                        <span className="badge text-bg-light">
                          {lastUpdate.odo.loading ? "Memuat..." : (selectedOdoMt?.nopol || "-")}
                        </span>
                      </div>
                      <div className="row g-2">
                        <div className="col-12 col-md-4">
                          <div className="page16-info-item page25-odo-info-item">
                            <small>Tanggal</small>
                            <strong className="d-inline-flex align-items-center gap-2">
                              {formatDate(lastUpdate.odo.data?.tanggal)}
                              {isTodayDate(lastUpdate.odo.data?.tanggal) && (
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  viewBox="0 0 24 24"
                                  fill="#16a34a"
                                  className="w-4 h-4"
                                  aria-hidden="true"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm14.03-2.53a.75.75 0 10-1.06-1.06l-4.22 4.22-2.22-2.22a.75.75 0 10-1.06 1.06l2.75 2.75a.75.75 0 001.06 0l4.75-4.75z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                              )}
                            </strong>
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="page16-info-item page25-odo-info-item">
                            <small>ODO</small>
                            <strong>{formatRibuan(lastUpdate.odo.data?.odo || 0)}</strong>
                          </div>
                        </div>
                        <div className="col-12 col-md-4">
                          <div className="page16-info-item page25-odo-info-item">
                            <small>KM Tempuh</small>
                            <strong>{formatRibuan(lastUpdate.odo.data?.kmt || 0)}</strong>
                          </div>
                        </div>
                      </div>

                      <div className="row g-3 mt-1">
                        <div className="col-12 col-md-4">
                          <label className="form-label">Update Tanggal</label>
                          <input type="date" className="form-control" value={panelForm[currentAction].tanggal} readOnly />
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Update ODO</label>
                          <input
                            type="number"
                            className={`form-control ${odoInvalidInput ? "field-required-invalid" : ""}`}
                            value={odoInputDisplayValue}
                            disabled={odoNormalLockedByCorrection}
                            onChange={(e) => updatePanelField("odo", "odoInput", e.target.value)}
                            placeholder="ODO terbaru.."
                          />
                          {odoNormalLockedByCorrection && (
                            <small className="text-muted">
                              ODO hari ini sudah dikoreksi.
                            </small>
                          )}
                          <label className="form-label mt-2">Nama Driver</label>
                          <input
                            type="text"
                            className="form-control"
                            value={panelForm.odo.namaDriver || ""}
                            onChange={(e) => updatePanelField("odo", "namaDriver", e.target.value)}
                            placeholder="Nama driver..."
                          />
                        </div>
                        <div className="col-12 col-md-4">
                          <label className="form-label">Jenis Transaksi</label>
                          <select
                            className="form-select"
                            value={panelForm.odo.jenisTransaksi || "normal"}
                            onChange={(e) => handleJenisTransaksiOdoChange(e.target.value)}
                          >
                            <option value="normal">Normal</option>
                            <option value="koreksi_odo">Koreksi Odo</option>
                            <option value="odo_rusak">Odo rusak</option>
                            <option value="mt_off">MT Off</option>
                          </select>
                          {panelForm.odo.jenisTransaksi === "koreksi_odo" && (
                            <div className="mt-2">
                              <label className="form-label small mb-1">Alasan Koreksi</label>
                              <textarea
                                rows={2}
                                className={`form-control ${odoInvalidAlasanKoreksi ? "field-required-invalid" : ""}`}
                                value={panelForm.odo.alasanKoreksi || ""}
                                onChange={(e) => updatePanelField("odo", "alasanKoreksi", e.target.value)}
                                placeholder="Masukkan alasan koreksi ODO..."
                              />
                              {odoInvalidAlasanKoreksi && (
                                <small className="text-muted">
                                  Alasan wajib diisi untuk transaksi Koreksi ODO.
                                </small>
                              )}
                            </div>
                          )}
                          {isOdoCatatanTransaction(panelForm.odo.jenisTransaksi) && (
                            <div className="mt-2">
                              <label className="form-label small mb-1">Catatan</label>
                              <textarea
                                rows={2}
                                className="form-control"
                                value={panelForm.odo.catatanFlag || ""}
                                onChange={(e) => updatePanelField("odo", "catatanFlag", e.target.value)}
                                placeholder="Tambahkan catatan..."
                              />
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="row g-3 mt-1">
                        <div className="col-12">
                          <div className="card page16-soft-card">
                            <div className="card-body">
                              <div className="d-flex justify-content-center">
                                <span>Upload Foto/Dokumen (opsional)</span>
                              </div>
                              <AttachmentPicker
                                attachments={attachmentsOdo}
                                setAttachments={setAttachmentsOdo}
                                bisaInput
                                invalidCaptionIndexes={invalidCaptionIndexesOdo}
                                onCaptionChange={() => {
                                  if (!invalidCaptionIndexesOdo.length) return;
                                  setInvalidCaptionIndexesOdo(collectInvalidCaptionIndexesOdo());
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="d-flex justify-content-end mt-3">
                        <button
                          type="button"
                          className="btn page16-gradient-btn text-white page25-submit-btn"
                          disabled={!odoInputDisplayValue || submitLocked.odo || odoNormalLockedByCorrection}
                          onClick={() => handleSimpanUpdate("odo")}
                        >
                          {submitLocked.odo ? "Tersimpan" : "Simpan Update ODO"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {currentAction === "td" && panelForm.td.mt && (
                  <div className="card page16-info-card mt-1">
                    
                    <div className="card-body">
                      <div className="d-flex justify-content-between align-items-center gap-2 mb-1">
                        <span className="page16-info-title">INSPEKSI BAN</span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-primary page25-formasi-toggle-btn"
                          disabled={tdLastUpdate.loading || !tdLastUpdate.rows.length}
                          onClick={openTdFormasiPopup}
                        >
                          Formasi Ban
                        </button>
                      </div>
                      <div className="row g-3">
                        <div className="col-12">
                          <div className="page25-td-list-wrap">
                            <div className="row g-2 mb-2">
                              <div className="col-6 col-lg-3">
                                <div className="page25-td-meta-item">
                                  <small>Nopol</small>
                                  <strong>{selectedTdMt?.nopol || "-"}</strong>
                                </div>
                              </div>
                              <div className="col-6 col-lg-3">
                                <div className="page25-td-meta-item">
                                  <small>Kapasitas</small>
                                  <strong>{tdLastUpdate?.kapasitas || "-"}</strong>
                                </div>
                              </div>
                              <div className="col-6 col-lg-3">
                                <div className="page25-td-meta-item">
                                  <small>Kategori</small>
                                  <strong>{tdLastUpdate?.kategori || "-"}</strong>
                                </div>
                              </div>
                              <div className="col-6 col-lg-3">
                                <div className="page25-td-meta-item">
                                  <small>Formasi</small>
                                  <strong>{Number(tdLastUpdate?.roda || 16)} Ban</strong>
                                </div>
                              </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="page16-info-title">{`INSPEKSI TGL ${formatDate(panelForm.td.tanggal)}`}</span>
                            </div>
                            <div className="page25-td-table-scroll">
                              <table className="table table-sm mb-0 page25-td-table">
                                <colgroup>
                                  <col className="page25-td-col-posisi" />
                                  <col className="page25-td-col-seri" />
                                  <col className="page25-td-col-tanggal" />
                                  <col className="page25-td-col-lasttd" />
                                  <col className="page25-td-col-input page25-td-col-group-start" />
                                  <col className="page25-td-col-input" />
                                  <col className="page25-td-col-input" />
                                  <col className="page25-td-col-input" />
                                  <col className="page25-td-col-baut-last page25-td-col-group-start" />
                                  <col className="page25-td-col-input" />
                                  <col className="page25-td-col-tekanan-last page25-td-col-group-start" />
                                  <col className="page25-td-col-tekanan-input" />
                                  <col className="page25-td-col-tekanan-input" />
                                  <col className="page25-td-col-temuan-last page25-td-col-group-start" />
                                  <col className="page25-td-col-temuan-input" />
                                  <col className="page25-td-col-kor page25-td-col-group-start" />
                                </colgroup>
                                <thead>
                                  <tr>
                                    <th rowSpan={2}>Posisi</th>
                                    <th rowSpan={2}>Seri Ban</th>
                                    <th rowSpan={2}>Tanggal</th>
                                    <th rowSpan={2}>Last TD</th>
                                    <th className="page25-td-group-start" colSpan={4}>Input TD</th>
                                    <th className="page25-td-group-start" colSpan={2}>Jumlah Baut</th>
                                    <th className="page25-td-group-start" colSpan={3}>Tekanan Ban (psi)</th>
                                    <th className="page25-td-group-start" colSpan={2}>Temuan</th>
                                    <th className="page25-td-group-start" rowSpan={2}>KOREKSI</th>
                                  </tr>
                                  <tr>
                                    <th className="page25-td-group-start">TD1</th>
                                    <th>TD2</th>
                                    <th>TD3</th>
                                    <th>TD4</th>
                                    <th className="page25-td-group-start">Last Cek</th>
                                    <th>Input</th>
                                    <th className="page25-td-group-start">Last Cek</th>
                                    <th>Sebelum</th>
                                    <th>Sesudah</th>
                                    <th className="page25-td-group-start">Last Check</th>
                                    <th>Input</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {tdLastUpdate.rows.map((row) => {
                                    const isCorrectionLocked = isTdCorrectionLocked(row.posisi);
                                    const isNormalLocked = isTdNormalLockedByCorrection(row.posisi);
                                    const inputLocked = isCorrectionLocked || isNormalLocked;
                                    const inputClassName = `form-control form-control-sm page25-td-cell-input rounded-0 ${inputLocked ? "bg-light" : ""}`.trim();
                                    const temuanValue = getTdAuxInputValueByPosisi(row.posisi, "temuan");
                                    const hasTemuan = Boolean(String(temuanValue || "").trim());
                                    const temuanAttachments = getTdInputRowByPosisi(row.posisi)?.temuanAttachments;
                                    const temuanAttachmentCount = Array.isArray(temuanAttachments)
                                      ? temuanAttachments.filter((item) => item?.file || item?.preview || String(item?.caption || "").trim()).length
                                      : 0;
                                    const lastTemuanText = String(row?.lastTemuan || "").trim();
                                    const lastTemuanFiles = Array.isArray(row?.lastTemuanFiles) ? row.lastTemuanFiles : [];
                                    const hasLastTemuan = Boolean(lastTemuanText || lastTemuanFiles.length);
                                    return (
                                      <tr
                                        key={row.posisi}
                                        className={panelForm.td.activePosisi === row.posisi ? "is-active" : ""}
                                        onClick={() => setActiveTdPosisi(row.posisi)}
                                        title={`Seri : ${String(row?.seri || "-")}`}
                                      >
                                        <td>{row.posisi}</td>
                                        <td className="page25-td-seri-cell" title={`Seri : ${String(row?.seri || "-")}`}>{row?.seri || "-"}</td>
                                        <td>
                                          <span className="d-inline-flex align-items-center gap-2">
                                            {formatDate(row.tanggal)}
                                            {isTodayDate(row.tanggal) && (
                                              <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                viewBox="0 0 24 24"
                                                fill="#16a34a"
                                                className="w-4 h-4"
                                                aria-hidden="true"
                                              >
                                                <path
                                                  fillRule="evenodd"
                                                  d="M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12zm14.03-2.53a.75.75 0 10-1.06-1.06l-4.22 4.22-2.22-2.22a.75.75 0 10-1.06 1.06l2.75 2.75a.75.75 0 001.06 0l4.75-4.75z"
                                                  clipRule="evenodd"
                                                />
                                              </svg>
                                            )}
                                          </span>
                                        </td>
                                        <td>{row.td}</td>
                                        <td className="page25-td-group-start" onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className={`${inputClassName} ${getTdRequiredInvalidClass(row.posisi, "td1")}`.trim()}
                                            value={getTdInputValueByPosisi(row.posisi, "td1")}
                                            disabled={inputLocked}
                                            onChange={(e) => updateTdRowByPosisi(row.posisi, "td1", e.target.value)}
                                          />
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className={`${inputClassName} ${getTdRequiredInvalidClass(row.posisi, "td2")}`.trim()}
                                            value={getTdInputValueByPosisi(row.posisi, "td2")}
                                            disabled={inputLocked}
                                            onChange={(e) => updateTdRowByPosisi(row.posisi, "td2", e.target.value)}
                                          />
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className={`${inputClassName} ${getTdRequiredInvalidClass(row.posisi, "td3")}`.trim()}
                                            value={getTdInputValueByPosisi(row.posisi, "td3")}
                                            disabled={inputLocked}
                                            onChange={(e) => updateTdRowByPosisi(row.posisi, "td3", e.target.value)}
                                          />
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className={`${inputClassName} ${getTdRequiredInvalidClass(row.posisi, "td4")}`.trim()}
                                            value={getTdInputValueByPosisi(row.posisi, "td4")}
                                            disabled={inputLocked}
                                            onChange={(e) => updateTdRowByPosisi(row.posisi, "td4", e.target.value)}
                                          />
                                        </td>
                                        <td className="page25-td-group-start">{row.bautLast ?? "-"}</td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className={inputClassName}
                                            value={getTdAuxInputValueByPosisi(row.posisi, "jumlahBaut")}
                                            disabled={inputLocked}
                                            onChange={(e) => updateTdAuxRowByPosisi(row.posisi, "jumlahBaut", e.target.value)}
                                          />
                                        </td>
                                        <td className="page25-td-group-start">{row.tekananLast ?? "-"}</td>
                                        <td className="page25-td-group-start" onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className={inputClassName}
                                            value={getTdAuxInputValueByPosisi(row.posisi, "tekananSebelum")}
                                            disabled={inputLocked}
                                            onChange={(e) => updateTdAuxRowByPosisi(row.posisi, "tekananSebelum", e.target.value)}
                                          />
                                        </td>
                                        <td className="page25-td-group-start" onClick={(e) => e.stopPropagation()}>
                                          <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            className={inputClassName}
                                            value={getTdAuxInputValueByPosisi(row.posisi, "tekananSesudah")}
                                            disabled={inputLocked}
                                            onChange={(e) => updateTdAuxRowByPosisi(row.posisi, "tekananSesudah", e.target.value)}
                                          />
                                        </td>
                                        <td className="page25-td-group-start" onClick={(e) => e.stopPropagation()}>
                                          <button
                                            type="button"
                                            className="btn btn-sm btn-outline-secondary page25-td-temuan-btn"
                                            disabled={!hasLastTemuan}
                                            title={hasLastTemuan ? "Lihat temuan last check" : "Tidak ada temuan last check"}
	                                            onClick={() => openTdLastTemuanModal(row)}
	                                          >
	                                            {hasLastTemuan ? "Lihat" : "Kosong"}
	                                          </button>
                                        </td>
                                        <td onClick={(e) => e.stopPropagation()}>
                                          <button
                                            type="button"
                                            className={`btn btn-sm page25-td-temuan-btn ${hasTemuan ? "btn-primary" : "btn-outline-secondary"}`.trim()}
                                            disabled={inputLocked}
                                            title={hasTemuan ? temuanValue : "Isi Temuan"}
                                            onClick={() => openTdTemuanModal(row.posisi)}
                                          >
                                            <span>Input</span>
                                            {temuanAttachmentCount > 0 && (
                                              <span className="page25-td-temuan-count">{temuanAttachmentCount}</span>
                                            )}
                                          </button>
                                        </td>
                                        <td className="page25-td-group-start" onClick={(e) => e.stopPropagation()}>
                                          <button
                                            type="button"
                                            className={`btn btn-sm ${isCorrectionLocked ? "btn-primary" : "btn-outline-primary"}`}
                                            title="Koreksi TD"
                                            onClick={() => openTdCorrectionModal(row.posisi)}
                                          >
                                            <i className={`fa fa-edit ${isCorrectionLocked ? "" : "text-white"}`} aria-hidden="true" />
                                          </button>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                            {isPusat && <div className="form-check form-switch mt-3">
                              <input
                                className="form-check-input"
                                type="checkbox"
                                role="switch"
                                id="td-no-significant-change"
                                checked={Boolean(panelForm.td.tdNoSignificantChange)}
                                disabled={!tdLastUpdate.rows.length}
                                onChange={(e) => handleTdNoSignificantChangeToggle(e.target.checked)}
                              />
                              <label className="form-check-label" htmlFor="td-no-significant-change">
                                Copy data TD
                              </label>
                            </div>}
                          </div>
                        </div>
                      </div>

                      {tdFormasiPopup.open && (
                        <div
                          className="page25-td-formasi-popup"
                          style={{ left: `${tdFormasiPopup.x}px`, top: `${tdFormasiPopup.y}px` }}
                          onMouseLeave={hideTdBanTooltip}
                        >
                          <div
                            className="page25-td-formasi-popup-header"
                            onMouseDown={startTdFormasiDrag}
                            onTouchStart={startTdFormasiDrag}
                          >
                            <span>Formasi Ban</span>
                            <button
                              type="button"
                              className="page25-td-formasi-close"
                              aria-label="Tutup formasi ban"
                              onMouseDown={(e) => e.stopPropagation()}
                              onTouchStart={(e) => e.stopPropagation()}
                              onClick={() => setTdFormasiPopup((prev) => ({ ...prev, open: false, dragging: false }))}
                            >
                              <i className="fa fa-times" aria-hidden="true" />
                            </button>
                          </div>
                          <div className="page25-td-formasi-wrap is-floating">
                            <FormasiBan
                              formasi={Number(tdLastUpdate?.roda || selectedTdMt?.roda || 16)}
                              cadangan={Number(tdLastUpdate?.cadangan || selectedTdMt?.cadangan || 3)}
                              onClick={setActiveTdPosisi}
                              daftarKodeBan={tdLastUpdate.banPositionMap}
                              onBanHover={showTdBanTooltip}
                              onBanLeave={hideTdBanTooltip}
                            />
                          </div>
                        </div>
                      )}

                      <div className="d-flex justify-content-end mt-3">
                        <button
                          type="button"
                          className="btn page16-gradient-btn text-white page25-submit-btn"
                          disabled={submitLocked.td || !tdCanSubmit}
                          onClick={() => handleSimpanUpdate("td")}
                        >
                          {submitLocked.td ? "Tersimpan" : "Simpan Update TD"}
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {currentAction === "bulk" && (
                  <div className="row g-3 mt-1">
                    <div className="col-12 col-lg-4">
                      <label className="form-label">Nama Cabang</label>
                      {renderCabangField("bulk", bulkInvalidCabang ? "field-required-invalid" : "")}
                    </div>
                    <div className="col-12 col-lg-4">
                      <label className="form-label">Tanggal</label>
                      <input
                        type="date"
                        className="form-control"
                        value={panelForm.bulk.tanggal}
                        readOnly
                      />
                    </div>
                    <div className="col-12">
                      {panelForm.bulk.kodeCabang && panelForm.bulk.tanggal && (
	                        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 border rounded px-3 py-2">
	                          <small className="text-muted">
	                            {bulkTemplateState.loading
	                              ? "Memuat data MT aktif..."
	                              : `MT Aktif di Cabang ${selectedBulkCabangNama} per ${formatDate(panelForm.bulk.tanggal)} sebanyak ${Number(bulkTemplateState.count || 0)} mt`}
	                          </small>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-sm"
                            disabled={bulkTemplateState.loading}
                            onClick={() => {
                              if (!panelForm.bulk.kodeCabang || !panelForm.bulk.tanggal) {
                                showNotif("error", "Pilih cabang dan tanggal terlebih dahulu.");
                                return;
                              }
                              showConfirm({
                                title: "Formulir Bulk Update ODO",
                                message: "Lanjutkan download formulir?",
                                onConfirm: async () => {
                                  await formBulkOdo({
                                    rows: bulkTemplateState.rows,
                                    namaCabang: bulkTemplateState.namaCabang || selectedBulkCabangNama,
                                    tanggal: panelForm.bulk.tanggal,
                                  });
                                },
                              });
                            }}
                          >
                            Download Formulir
                          </button>
                        </div>
                      )}
                    </div>
                    <div className="col-12 col-lg-4">
                      <label className="form-label">Jumlah Baris Valid</label>
                      <input
                        className="form-control text-end"
                        value={Number(bulkUploadState.valid.length || 0)}
                        readOnly
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">File Excel (Bulk)</label>
                      <input
                        ref={bulkFileInputRef}
                        type="file"
                        className={`form-control ${bulkInvalidFile ? "field-required-invalid" : ""}`}
                        accept=".xls,.xlsx"
                        onChange={async (e) => {
                          const file = e.target.files?.[0] || null;
                          setPanelForm((prev) => ({
                            ...prev,
                            bulk: { ...prev.bulk, file },
                          }));
                          setSubmitLocked((prev) => ({ ...prev, bulk: false }));
                          if (!file) {
                            setBulkUploadState({
                              parsing: false,
                              valid: [],
                              invalid: [],
                              totalReadRows: 0,
                              kmHeader: "",
                              headerDate: "",
                              headerError: "",
                              validated: false,
                            });
                            return;
                          }
                          setBulkUploadState((prev) => ({
                            ...prev,
                            parsing: true,
                            valid: [],
                            invalid: [],
                            totalReadRows: 0,
                            headerDate: "",
                            headerError: "",
                            validated: false,
                          }));
	                          try {
	                            const parsed = await uploadBulkOdo(file);
	                            const parsedValid = Array.isArray(parsed?.valid) ? parsed.valid : [];
	                            const parsedInvalid = Array.isArray(parsed?.invalid) ? parsed.invalid : [];
	                            const nextValid = [];
	                            const nextInvalid = [...parsedInvalid];
	                            parsedValid.forEach((item) => {
	                              const idMt = String(item?.id_mt || "").trim();
	                              const meta = bulkMtMetaById?.[idMt];
	                              const jumlahRoda = Number(meta?.roda || 0);
	                              const jumlahBanPosisiAktif = countBulkPosisiPActive(meta?.daftar_ban);
	                              if (meta?.hasKoreksiOdoHariIni) {
	                                nextInvalid.push({
	                                  ...item,
	                                  reason: buildKoreksiOdoHariIniBulkNotice(meta?.nopol || item?.nopol),
	                                });
	                                return;
	                              }
	                              if (!meta || !Number.isFinite(jumlahRoda) || jumlahRoda <= 0) {
	                                nextInvalid.push({
	                                  ...item,
	                                  reason: "Data formasi MT tidak ditemukan/tidak sesuai.",
	                                });
	                                return;
	                              }
	                              if (jumlahBanPosisiAktif !== jumlahRoda) {
	                                nextInvalid.push({
	                                  ...item,
	                                  reason: "Input ban belum sesuai formasi.",
	                                });
	                                return;
	                              }
	                              nextValid.push(item);
	                            });
                            const autoValidated = Boolean(
                              !String(parsed?.headerError || "").trim()
                              && String(parsed?.headerDate || "").trim() === String(panelForm.bulk.tanggal || "").trim()
                              && nextValid.length > 0
                              && nextInvalid.length === 0
                            );
                            setBulkUploadState({
                              parsing: false,
                              valid: nextValid,
                              invalid: nextInvalid,
                              totalReadRows: Number(parsed?.totalReadRows || (nextValid.length + nextInvalid.length) || 0),
                              kmHeader: String(parsed?.kmHeader || ""),
                              headerDate: String(parsed?.headerDate || ""),
                              headerError: String(parsed?.headerError || ""),
                              validated: autoValidated,
                            });
                            if (autoValidated) {
                              showNotif("success", `Validasi berhasil otomatis. ${nextValid.length} baris valid.`);
                            }
                          } catch (parseErr) {
                            console.error(parseErr);
                            setBulkUploadState({
                              parsing: false,
                              valid: [],
                              invalid: [],
                              totalReadRows: 0,
                              kmHeader: "",
                              headerDate: "",
                              headerError: "Gagal membaca file Excel.",
                              validated: false,
                            });
                          }
                        }}
                      />
                      <small className="text-muted d-block mt-1">
                        {bulkUploadState.parsing
                          ? "Memvalidasi file..."
                          : (
                            bulkUploadState.headerError
                              || (bulkUploadState.kmHeader
                                ? `Data Upload: ${bulkUploadState.kmHeader}`
                                : "Upload file formulir untuk validasi.")
                          )}
                      </small>
                      {!bulkUploadState.headerError && bulkUploadState.kmHeader && !bulkHeaderDateMatches && (
                        <small className="text-danger d-block mt-1">
                          {`Tanggal header file (${bulkUploadState.headerDate || "-"}) tidak sama dengan tanggal input (${panelForm.bulk.tanggal || "-"})`}
                        </small>
                      )}
                      {bulkUploadState.validated && (
                        <small className="text-success d-block mt-1">
                          Data valid dan siap diupload.
                        </small>
                      )}
                      {bulkUploadState.invalid.length > 0 && (
                        <small className="text-danger d-block mt-1">
                          {`Data Invalid: Nopol ${bulkUploadState.invalid[0]?.nopol || "-"} - ${bulkUploadState.invalid[0]?.reason || "Tidak valid"}`}
                        </small>
                      )}
                    </div>
                  </div>
                )}

                <div className="d-flex justify-content-end gap-2 mt-4">
                  {currentAction === "bulk" && (
                    <button
                      type="button"
                      className="btn page16-gradient-btn text-white"
                      disabled={
                        !panelForm.bulk.file
                        || submitLocked.bulk
                        || bulkUploadState.parsing
                        || Boolean(bulkUploadState.headerError)
                        || !bulkHeaderDateMatches
                        || bulkUploadState.valid.length <= 0
                        || bulkUploadState.invalid.length > 0
                      }
                      onClick={() => handleSimpanUpdate("bulk")}
                    >
                      {submitLocked.bulk ? "Tersimpan" : (selectedAction?.submitLabel || "Simpan")}
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {tdLastTemuanModal.open && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(15, 23, 42, 0.45)" }}>
          <div className="modal-dialog modal-md modal-dialog-centered" role="document">
            <div className="modal-content page25-td-last-temuan-modal">
              <div className="modal-header">
                <h5 className="modal-title">{`Last Check Temuan TD ${tdLastTemuanModal.posisi || ""}`}</h5>
              </div>
              <div className="modal-body">
                <div className="mb-3">
                  <label className="form-label">Temuan Sebelumnya</label>
                  <textarea
                    rows={4}
                    className="form-control page25-td-temuan-textarea"
                    value={tdLastTemuanModal.temuan || "Tidak ada catatan temuan."}
                    readOnly
                  />
                </div>
                <div className="d-flex align-items-center justify-content-between gap-2">
                  <span className="small text-muted">
                    {tdLastTemuanModal.files.length
                      ? `${tdLastTemuanModal.files.length} dokumen tersedia`
                      : "Tidak ada dokumen sebelumnya"}
                  </span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary"
                    disabled={!tdLastTemuanModal.files.length}
                    onClick={() => setTdLastTemuanPreviewOpen(true)}
                  >
                    Lihat Dokumen
                  </button>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeTdLastTemuanModal}
                >
                  Tutup
                </button>
              </div>
            </div>
          </div>
          {tdLastTemuanPreviewOpen && (
            <ShowPic
              open={tdLastTemuanPreviewOpen}
              onClose={() => setTdLastTemuanPreviewOpen(false)}
              row={{ catatan: tdLastTemuanModal.temuan || "" }}
              backendPort={backendPort}
              token=""
              onUploadSuccess={() => {}}
              readOnly
              customTitle={`Dokumen Temuan TD ${tdLastTemuanModal.posisi || ""}`}
              customNote={tdLastTemuanModal.temuan || ""}
              customFiles={tdLastTemuanModal.files}
              zIndex={1090}
            />
          )}
        </div>
      )}
      {tdTemuanModal.open && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(15, 23, 42, 0.45)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content page25-td-temuan-modal">
              <div className="modal-header">
                <h5 className="modal-title">{`Temuan TD ${tdTemuanModal.posisi || ""}`}</h5>
	              </div>
	              <div className="modal-body">
	                <div className="mb-3">
	                  <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
	                    <label className="form-label mb-0">Temuan (wajib)</label>
	                    <button
	                      type="button"
	                      className="btn btn-sm btn-outline-secondary"
	                      disabled={!String(tdTemuanModal.temuan || "").trim() && !getTdLastTemuanTextByPosisi(tdTemuanModal.posisi)}
	                      onClick={toggleTdTemuanFromLastCheck}
	                    >
	                      {String(tdTemuanModal.temuan || "").trim() ? "Kosongkan Input" : "Copy Temuan Sebelumnya"}
	                    </button>
	                  </div>
	                  <textarea
	                    rows={4}
	                    className={`form-control page25-td-temuan-textarea ${!String(tdTemuanModal.temuan || "").trim() ? "field-required-invalid" : ""}`}
                    value={tdTemuanModal.temuan}
                    onChange={(e) => setTdTemuanModal((prev) => ({ ...prev, temuan: e.target.value }))}
                    placeholder="Masukkan detail temuan pada posisi ban ini..."
                    autoFocus
                  />
                </div>
                <div>
                  <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                    <label className="form-label mb-0">Upload Dokumen (opsional)</label>
                    <span className="text-muted small">Tidak wajib diisi</span>
                  </div>
                  <AttachmentPicker
                    attachments={tdTemuanModal.attachments}
                    setAttachments={updateTdTemuanAttachments}
                    bisaInput
                    invalidCaptionIndexes={[]}
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={closeTdTemuanModal}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!isTdTemuanDraftValid()}
                  onClick={applyTdTemuanModal}
                >
                  Simpan Temuan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {tdCorrectionModal.open && (
        <div className="modal d-block" tabIndex="-1" role="dialog" style={{ background: "rgba(15, 23, 42, 0.45)" }}>
          <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{`Koreksi TD ${tdCorrectionModal.posisi || ""}`}</h5>
              </div>
              <div className="modal-body">
                <div className="row g-3">
                  <div className="col-12">
                    <label className="form-label">Alasan Koreksi</label>
                    <textarea
                      rows={3}
                      className={`form-control ${!String(tdCorrectionModal.alasan || "").trim() ? "field-required-invalid" : ""}`}
                      value={tdCorrectionModal.alasan}
                      onChange={(e) => updateTdCorrectionDraft("alasan", e.target.value)}
                      placeholder="Masukkan alasan koreksi TD..."
                      autoFocus
                    />
                  </div>
                  <div className="col-12">
                    <label className="form-label">Upload Memo (wajib)</label>
                    <input
                      type="file"
                      accept=".jpg,.jpeg,.png,.webp,.pdf"
                      className={`form-control ${!(tdCorrectionModal.memo instanceof File) ? "field-required-invalid" : ""}`}
                      onChange={(e) => updateTdCorrectionMemo(e.target.files?.[0] || null)}
                    />
                    <div className="form-text">
                      {tdCorrectionModal.memo instanceof File
                        ? `Memo dipilih: ${tdCorrectionModal.memo.name}`
                        : "Format JPG, PNG, WebP, atau PDF. Maksimal 5 MB."}
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Last TD</label>
                    <div className="row g-2">
                      {["td1", "td2", "td3", "td4"].map((field, idx) => {
                        const lastParts = getLastTdPartsByPosisi(tdCorrectionModal.posisi);
                        return (
                          <div className="col-6 col-md-3" key={`last-${field}`}>
                            <input
                              type="text"
                              className="form-control text-center bg-light"
                              value={lastParts?.[idx] ?? ""}
                              readOnly
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div className="col-12 col-lg-6">
                    <label className="form-label">Input TD</label>
                    <div className="row g-2">
                      {["td1", "td2", "td3", "td4"].map((field, idx) => {
                        const value = String(tdCorrectionModal?.[field] ?? "").trim();
                        const nextValue = Number(value);
                        const isInvalid = !/^\d+$/.test(value) || !Number.isFinite(nextValue) || nextValue > 16;
                        return (
                          <div className="col-6 col-md-3" key={`input-${field}`}>
                            <input
                              type="text"
                              inputMode="numeric"
                              pattern="[0-9]*"
                              className={`form-control text-center ${isInvalid ? "field-required-invalid" : ""}`}
                              value={tdCorrectionModal[field]}
                              placeholder={`TD${idx + 1}`}
                              onChange={(e) => updateTdCorrectionDraft(field, e.target.value)}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => setTdCorrectionModal({
                    open: false,
                    posisi: "",
                    alasan: "",
                    td1: "",
                    td2: "",
                    td3: "",
                    td4: "",
                    memo: null,
                  })}
                >
                  Batal
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary"
                  onClick={() => clearTdCorrectionForPosisi()}
                >
                  Clear
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!isTdCorrectionDraftValid()}
                  onClick={applyTdCorrectionModal}
                >
                  Lanjutkan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <ModalCariMT
        open={showCariMtModal}
        onClose={() => {
          setShowCariMtModal(false);
          setMtPickerTarget("");
        }}
        showNotif={showNotif}
        onSelect={(mt) => {
          const targetKey = mtPickerTarget || currentAction;
          if (targetKey === "odo" || targetKey === "td") {
            updatePanelField(targetKey, "mt", String(mt?.id_mt || "").trim());
          }
          setShowCariMtModal(false);
          setMtPickerTarget("");
        }}
        title="Pilih No. Pol MT"
        globalOpsi={globalOpsi}
        kodeCabang={
          panelForm[mtPickerTarget || currentAction]?.kodeCabang
          || (!isPusat ? actorKodeCabang : "")
        }
      />
    </div>
  );
}
